import { useMemo } from "react";
import { keepPreviousData, useMutation, useMutationState, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { extractApiError } from "@/features/tech_calc/utils";
import { useAuthStore } from "@/store/auth";
import { NAV_BADGES_KEY } from "@/hooks/useNavBadges";
import { careApi } from "@/features/clients/api/care";
import * as api from "./api";
import { apiErr } from "./utils";
import { pobierzRysunki } from "./cadViewer";
import type {
  StageProceedingInput,
  AccessBasisType, AsyncTaskRecord, CaseCloseValues, ChangeAnalysisRequest, DesignerInput, DocAnonymization, DxfLayerCatalogEntry, DxfLayerPreset,
  FormatkaSheetSet, GlobalKpiFilters, GlobalKpiListRequest, KpiActionItem, KpiReview, KwOwnerRow,
  Office, OwnerAccess, Parcel, ParcelAccessMode, ParcelExportFilters, Proceeding, SheetFrame,
  WpwHandover, WpwMailBulkAction, WpwMassOverview, WpwMassScopeReview, WypisPageEntry, WypisProposalParcel, ZEAnnexRequest, ZEDocFormat, ZEReport
} from "./types";

export const qk = {
  portfolio: ["pp", "portfolio"] as const,
  portfolioMilestoneColumn: (key: string) => ["pp", "portfolio-milestone-column", key] as const,
  portfolioReportColumn: ["pp", "portfolio-report-column"] as const,
  portfolioDeepSearch: (q: string) => ["pp", "portfolio-deep-search", q] as const,
  connection: (id: string) => ["pp", "connection", id] as const,
  care: (id: string) => ["pp", "care", id] as const,
  attention: (id: string) => ["pp", "attention", id] as const,
  byProjectRoot: ["pp", "by-project"] as const,
  byProject: (pid: string) => ["pp", "by-project", pid] as const,
  projectContractors: (c: string) => ["pp", "project-contractors", c] as const,
  contractorOrders: (c: string, k: string) => ["pp", "contractor-orders", c, k] as const,
  variants: (c: string) => ["pp", "variants", c] as const,
  projectEvents: (c: string, f: Record<string, string>) => ["pp", "project-events", c, f] as const,
  parcelRouteSegments: (c: string, f: Record<string, string>) => ["pp", "parcel-route-segments", c, f] as const,
  dxfLayerPresets: ["pp", "dxf-layer-presets"] as const,
  dxfLayersRoot: (preset: string) => ["pp", "dxf-layers", preset] as const,
  dxfLayers: (preset: string, activeOnly: boolean) => ["pp", "dxf-layers", preset, activeOnly] as const,
  parcels: (c: string) => ["pp", "parcels", c] as const,
  parcel: (id: string) => ["pp", "parcel", id] as const,
  // Pod prefiksem ["pp", "parcel"], żeby invalidateAccessGraph odświeżał tabelę razem z szufladą.
  parcelStatusMatrix: (id: string) => ["pp", "parcel", id, "status-matrix"] as const,
  proceedingLinkCandidates: (id: string) => ["pp", "proceeding-link-candidates", id] as const,
  resolutionParcels: (id: string) => ["pp", "resolution-parcels", id] as const,
  parcelHistory: (id: string) => ["pp", "parcel-history", id] as const,
  parcelNotes: (id: string) => ["pp", "parcel-notes", id] as const,
  // access titles (OwnerAccess)
  accessBasisTypes: (activeOnly: boolean) => ["pp", "access-basis-types", activeOnly] as const,
  ownerAccesses: (f: Record<string, string>) => ["pp", "owner-accesses", f] as const,
  ownerAccessEvents: (id: string) => ["pp", "owner-access-events", id] as const,
  // proceedings (correspondence threads)
  proceedings: (f: Record<string, string>) => ["pp", "proceedings", f] as const,
  proceeding: (id: string) => ["pp", "proceeding", id] as const,
  proceedingTypes: (activeOnly: boolean) => ["pp", "proceeding-types", activeOnly] as const,
  // per-project permissions
  projectPermissions: (c: string) => ["pp", "project-permissions", c] as const,
  linkableOrders: (p: Record<string, string>) => ["pp", "linkable-orders", p] as const,
  kpiReviews: (c: string) => ["pp", "kpi-reviews", c] as const,
  kpiItemPings: (id: string) => ["pp", "kpi-item-pings", id] as const,
  kpiComments: (r: string) => ["pp", "kpi-comments", r] as const,
  reports: (c: string) => ["pp", "reports", c] as const,
  reportSchedule: (c: string) => ["pp", "report-schedule", c] as const,
  annexRequests: (c: string) => ["pp", "annex-requests", c] as const,
  offices: ["pp", "offices"] as const,
  presets: ["pp", "metadata-presets"] as const,
  designers: ["pp", "designers"] as const,
  docTemplates: ["pp", "doc-templates"] as const,
  docTemplateVersions: (id: string) => ["pp", "doc-template-versions", id] as const,
  docVariables: ["pp", "doc-variables"] as const,
  docAnonymizations: ["pp", "doc-anonymizations"] as const,
  docAnonymizationModels: ["pp", "doc-anonymization-models"] as const,
  docTemplateCheck: (t: string, kind: string, ctx: string) => ["pp", "doc-template-check", t, kind, ctx] as const,
  contextOperator: (kind: string, ctx: string) => ["pp", "context-operator", kind, ctx] as const,
  generatedDocsRoot: ["pp", "generated-docs"] as const,
  generatedDocs: (kind: string, ids: string[]) => ["pp", "generated-docs", kind, ...ids] as const,
  regulatoryChecklist: (parcelId: string) => ["pp", "regulatory-checklist", parcelId] as const,
  // DLC_02 register
  register: (p?: Record<string, string>) => ["pp", "correspondence-register", p ?? {}] as const,
  // WPW handover (działka | WPW)
  wpwHandovers: (f: Record<string, string>) => ["pp", "wpw-handovers", f] as const,
  wpwNeverSent: (connectionId: string) => ["pp", "wpw-never-sent", connectionId] as const,
  // design stage (Projekt techniczny / Kosztorys)
  technicalProject: (c: string) => ["pp", "technical-project", c] as const,
  costEstimate: (c: string) => ["pp", "cost-estimate", c] as const,
  // wypisy (land registry excerpts)
  wypisy: (c: string) => ["pp", "wypisy", c] as const,
  // wnioski urzędowe (silnik pakietów)
  appRegistry: ["pp", "app-registry"] as const,
  appOverview: (c: string) => ["pp", "app-overview", c] as const,
  applications: (f: Record<string, string>) => ["pp", "applications", f] as const,
  application: (id: string) => ["pp", "application", id] as const,
  poas: (f: Record<string, string>) => ["pp", "poas", f] as const,
  poasExpiring: ["pp", "poas-expiring"] as const,
  poaClientConfig: (key: string) => ["pp", "poa-client-config", key] as const,
  poaOfficeConfig: (officeId: string) => ["pp", "poa-office-config", officeId] as const,
  poaConfigs: ["pp", "poa-configs"] as const,
  poaProjects: (client: string) => ["pp", "poa-projects", client] as const,
  clientBranches: (client: string) => ["pp", "client-branches", client] as const,
  decisions: (f: Record<string, string>) => ["pp", "decisions", f] as const,
  appAutoMaps: (id: string) => ["pp", "app-auto-maps", id] as const,
  poaFeeCandidates: ["pp", "poa-fee-candidates"] as const,
  poaFeeSuggestion: (id: string) => ["pp", "poa-fee-suggestion", id] as const,
  // wypis orders (zamawianie wypisów EGiB)
  wypisCandidates: (f: Record<string, string>) => ["pp", "wypis-candidates", f] as const,
  wypisOrders: (f: Record<string, string>) => ["pp", "wypis-orders", f] as const,
  wypisOrder: (id: string) => ["pp", "wypis-order", id] as const,
  mzOrders: (f: Record<string, string>) => ["pp", "mz-orders", f] as const,
  mdcpOrders: (f: Record<string, string>) => ["pp", "mdcp-orders", f] as const,
  mzOrder: (id: string) => ["pp", "mz-order", id] as const,
  mzSuggestContext: (connectionId: string) => ["pp", "mz-suggest-context", connectionId] as const,
  // mpzp orders (wypis i wyrys z planu miejscowego — urząd gminy)
  mpzpCandidates: (f: Record<string, string>) => ["pp", "mpzp-candidates", f] as const,
  mpzpOrders: (f: Record<string, string>) => ["pp", "mpzp-orders", f] as const,
  mpzpOrder: (id: string) => ["pp", "mpzp-order", id] as const,
  mpzpOrdersForParcel: (parcelId: string) => ["pp", "mpzp-orders-for-parcel", parcelId] as const,
  // preanaliza dokumentów przysłanych przez urzędy
  officeDocScans: (f: Record<string, string>) => ["pp", "office-docs", f] as const,
  officeDocBatches: ["pp", "office-doc-batches"] as const,
  // connection file attachments
  connectionFiles: (connectionId: string) => ["pp", "connection-files", connectionId] as const,
  // project folder tree (auto + manual)
  projectFolders: (connectionId: string) => ["pp", "project-folders", connectionId] as const,
  rysunki: (connectionId: string) => ["pp", "rysunki", connectionId] as const,
  // zamówienia do wyboru przy „udostępnij w zleceniu" (zakładka Pliki)
  shareableOrders: (connectionId: string) => ["pp", "shareable-orders", connectionId] as const,
  // milestone notes thread
  milestoneNotes: (connectionId: string, mkey: string) =>
    ["pp", "milestone-notes", connectionId, mkey] as const,
  // formatki (plan sytuacyjny)
  sheetSets: (c: string) => ["pp", "formatka-sheet-sets", c] as const,
  sheetSet: (id: string) => ["pp", "formatka-sheet-set", id] as const,
  formatkaTemplates: ["pp", "formatka-templates"] as const,
  formatkaTemplateVersions: (id: string) => ["pp", "formatka-template-versions", id] as const,
  formatkaVariables: ["pp", "formatka-variables"] as const,
  formatkaDefaults: ["pp", "formatka-defaults"] as const,
  formatkaCheck: (t: string, s: string) => ["pp", "formatka-check", t, s] as const,
  formatkaFieldDefaults: ["pp", "formatka-field-defaults"] as const,
  task: (id: string) => ["bg-task", id] as const,
};

// The workspace page loads a connection via `by-project`, while detail/portfolio
// flows use `connection(id)` — both cache the same entity. Any mutation that
// changes a connection must invalidate both keys (by-project matched by prefix),
// otherwise the workspace shows stale data until a full page reload.
const invalidateConnection = (qc: QueryClient, connId: string) => {
  qc.invalidateQueries({ queryKey: qk.connection(connId) });
  qc.invalidateQueries({ queryKey: qk.byProjectRoot });
};

// Milestone board and the advisor's DAG/control views share the same underlying
// steps: changing a milestone (state, add, delete, reorder) must also refresh the
// process graph + control board so the DAG recolors live — no manual F5 needed.
const invalidateMilestoneGraph = (qc: QueryClient, connId: string) => {
  invalidateConnection(qc, connId);
  qc.invalidateQueries({ queryKey: ["pp", "process-graph", connId] });
  qc.invalidateQueries({ queryKey: ["pp", "control", connId] });
  qc.invalidateQueries({ queryKey: ["pp", "todo", connId] });
  qc.invalidateQueries({ queryKey: ["pp", "graph-proposals", connId] });
};

// Access/proceeding mutations can flip parcel auto-status → refresh parcels, the parcel
// drawer, parcel history, the timeline and the connection (progress tiles).
const invalidateAccessGraph = (qc: QueryClient, connId: string) => {
  qc.invalidateQueries({ queryKey: qk.parcels(connId) });
  qc.invalidateQueries({ queryKey: ["pp", "parcel"] });
  qc.invalidateQueries({ queryKey: ["pp", "parcel-history"] });
  qc.invalidateQueries({ queryKey: ["pp", "owner-accesses"] });
  qc.invalidateQueries({ queryKey: ["pp", "proceedings"] });
  qc.invalidateQueries({ queryKey: ["pp", "project-events", connId] });
  invalidateConnection(qc, connId);
};

// ── portfolio + connection ──────────────────────────────────────────────────
export const usePortfolio = (enabled = true) =>
  useQuery({ queryKey: qk.portfolio, queryFn: api.getPortfolio, enabled });

// Lista wyboru projektu: wiersze kompaktowe (id, nazwa, numer umowy…). Własny klucz POD
// qk.portfolio — unieważnienia portfela obejmują i ten, a strona portfela nie dostanie
// okrojonych wierszy z cache.
export const usePortfolioOptions = (enabled = true) =>
  useQuery({ queryKey: [...qk.portfolio, "compact"], queryFn: api.getPortfolioCompact, enabled });

// Kolumna „Kamień" w portfelu: pusty klucz = sam katalog kroków do listy wyboru.
export const usePortfolioMilestoneColumn = (key: string) =>
  useQuery({
    queryKey: qk.portfolioMilestoneColumn(key),
    queryFn: () => api.getPortfolioMilestoneColumn(key || undefined),
    // Po zmianie kroku lista wyboru ma nie migać — poprzednia odpowiedź zostaje na
    // ekranie do czasu nowej (stany kolumny bierzemy tylko gdy `key` się zgadza).
    placeholderData: (prev) => prev,
  });

// Kolumna „Najbliższy raport" w portfelu — dociągana dopiero, gdy użytkownik ją włączy.
// Zawężenia (obowiązek raportowania, okno terminu) idą do serwera — dzięki temu komórka
// dostaje wszystkie terminy z okna, a zapytanie da się powtórzyć poza tym widokiem.
export const usePortfolioReportColumn = (
  enabled: boolean,
  params: import("./types").ReportColumnParams = {},
) =>
  useQuery({
    queryKey: [...qk.portfolioReportColumn, params],
    queryFn: () => api.getPortfolioReportColumn(params),
    enabled,
  });

/** Minimalna długość frazy dla szukania po danych powiązanych (lustro deep_search.MIN_QUERY_LEN). */
export const DEEP_SEARCH_MIN_LEN = 2;

// Pole „Szukaj" w portfelu poza nagłówkiem projektu: działki, KW, wnioski WNU/pakiety WPW,
// zamówienia, nazwy plików, kamienie, warstwy DXF. Frazę podaje się JUŻ ODROCZONĄ (portfel
// debounce'uje wpisywanie) — serwer przeczesuje kilka tabel, więc nie na każdy znak.
export const usePortfolioDeepSearch = (query: string) => {
  const q = query.trim();
  return useQuery({
    queryKey: qk.portfolioDeepSearch(q),
    queryFn: () => api.getPortfolioDeepSearch(q),
    enabled: q.length >= DEEP_SEARCH_MIN_LEN,
    // Wynik poprzedniej frazy zostaje do czasu nowego — inaczej przy każdym znaku
    // doklejone wiersze znikałyby i wracały.
    placeholderData: (prev) => prev,
  });
};

export const useConnection = (id: string | undefined) =>
  useQuery({ queryKey: qk.connection(id ?? ""), queryFn: () => api.getConnection(id!), enabled: !!id });

export const useConnectionByProject = (projectId: string | undefined) =>
  useQuery({
    queryKey: qk.byProject(projectId ?? ""),
    queryFn: () => api.getConnectionByProject(projectId!),
    enabled: !!projectId,
  });

// Karta specyfiki („trudny klient") — dociągana leniwie po otwarciu dymka opieki.
export const useConnectionCare = (connectionId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.care(connectionId ?? ""),
    queryFn: () => api.getConnectionCare(connectionId!),
    enabled: enabled && !!connectionId,
  });

export const useAddConnectionCareNote = (connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.addConnectionCareNote(connectionId, text),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.care(connectionId) }),
  });
};

// Zmiana danych karty specyfiki dotyka wielu powierzchni: badge (connection),
// portfel, cache modułu Klienci i selektor oddziałów — odświeżamy wszystko naraz.
const invalidateCareEverywhere = (qc: QueryClient, connectionId: string) => {
  qc.invalidateQueries({ queryKey: qk.care(connectionId) });
  invalidateConnection(qc, connectionId);
  qc.invalidateQueries({ queryKey: qk.portfolio });
  qc.invalidateQueries({ queryKey: ["client-care"] });
  qc.invalidateQueries({ queryKey: ["clients"] });
  qc.invalidateQueries({ queryKey: ["client-branches"] });
  qc.invalidateQueries({ queryKey: ["client-sub-branches"] });
  qc.invalidateQueries({ queryKey: ["pp", "care-overview"] });
};

// Pierwsza flaga „szczególnej opieki" dodawana WPROST z projektu (dymek / zakładka
// Opieka) — bez wycieczki do kartoteki klienta.
export const useAddConnectionCareFlag = (connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      client?: string | null;
      branch?: string | null;
      sub_branch?: string | null;
      label: string;
      reason?: string;
    }) => careApi.flags.create(payload),
    onSuccess: () => invalidateCareEverywhere(qc, connectionId),
  });
};

// Rejestr „szczególnych oddziałów" (strona hubu) — lista wszystkich poziomów z kartą.
export const useCareOverview = (enabled = true) =>
  useQuery({ queryKey: ["pp", "care-overview"] as const, queryFn: api.getCareOverview, enabled });

// Trwające projekty oddziału bez kamienia danego ustalenia — podgląd przed
// potwierdzeniem („dodaj do trwających…").
export const useCareAffected = (arrangementId: string | null) =>
  useQuery({
    queryKey: ["pp", "care-affected", arrangementId ?? ""] as const,
    queryFn: () => careApi.arrangements.affected(arrangementId!),
    enabled: !!arrangementId,
  });

export const useCareApply = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, connections }: { id: string; connections?: string[] }) =>
      careApi.arrangements.apply(id, connections),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["pp", "care-affected", id] });
      // kamienie doszły do projektów — profile, tablice i portfel są nieaktualne
      qc.invalidateQueries({ queryKey: ["pp"] });
    },
  });
};

// Komplet operacji zakładki „Opieka": wygaszanie/usuwanie flag, CRUD ustaleń,
// kasowanie wpisów dziennika. Wszystkie przez wspólne API modułu Klienci
// (uprawnienia egzekwuje backend), z pełnym odświeżeniem powierzchni.
export const useCareEntryMutations = (connectionId: string) => {
  const qc = useQueryClient();
  const done = () => invalidateCareEverywhere(qc, connectionId);
  const updateFlag = useMutation({
    mutationFn: ({ id, ...data }: { id: string; label?: string; reason?: string; is_active?: boolean }) =>
      careApi.flags.update(id, data),
    onSuccess: done,
  });
  const removeFlag = useMutation({
    mutationFn: (id: string) => careApi.flags.delete(id),
    onSuccess: done,
  });
  const createArrangement = useMutation({
    mutationFn: (payload: {
      branch?: string | null;
      sub_branch?: string | null;
      kind: "warning" | "required_step";
      text?: string;
      step_label?: string;
      // SLA kroku: faza 1 = nasza akcja, faza 2 = odpowiedź strony (null = bez terminu).
      submit_sla_days?: number | null;
      resolve_sla_days?: number | null;
    }) => careApi.arrangements.create(payload),
    onSuccess: done,
  });
  const updateArrangement = useMutation({
    mutationFn: ({ id, ...data }: { id: string; is_active?: boolean; text?: string; step_label?: string }) =>
      careApi.arrangements.update(id, data),
    onSuccess: done,
  });
  const removeArrangement = useMutation({
    mutationFn: (id: string) => careApi.arrangements.delete(id),
    onSuccess: done,
  });
  const removeNote = useMutation({
    mutationFn: (id: string) => careApi.notes.delete(id),
    onSuccess: done,
  });
  return { updateFlag, removeFlag, createArrangement, updateArrangement, removeArrangement, removeNote };
};

// Podwykonawcy przypisani do projektu (zebrani z zamówień) — zakładka „Podwykonawcy".
export const useProjectContractors = (connectionId: string | undefined) =>
  useQuery({
    queryKey: qk.projectContractors(connectionId ?? ""),
    queryFn: () => api.getProjectContractors(connectionId!),
    enabled: !!connectionId,
  });

// Zamówienia jednego podwykonawcy (z danymi zapotrzebowania + załącznikami) — pobierane
// leniwie dopiero po rozwinięciu wiersza w zakładce „Podwykonawcy".
export const useContractorOrders = (
  connectionId: string | undefined,
  contractorId: string | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: qk.contractorOrders(connectionId ?? "", contractorId ?? ""),
    queryFn: () => api.getContractorOrders(connectionId!, contractorId!),
    enabled: !!connectionId && !!contractorId && enabled,
  });

/** Podpowiedź strefy dla dialogu „Nowy projekt ZE” — odświeżana po wyborze przedsięwzięcia. */
export const useSuggestedSrid = (projectId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "suggest-srid", projectId ?? ""] as const,
    queryFn: () => api.suggestSrid(projectId!),
    enabled: !!projectId,
  });

export const useCreateConnection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.createConnection>[0]) => api.createConnection(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.portfolio }),
  });
};

export const useUpdateConnection = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.updateConnection>[1]) => api.updateConnection(id, payload),
    onSuccess: (_data, variables) => {
      invalidateConnection(qc, id);
      qc.invalidateQueries({ queryKey: qk.portfolio });
      // suggested_next_date is carried in the KPI-reviews payload and is derived from
      // kpi_interval_days, so a change to the interval must refresh that query too.
      if (variables && "kpi_interval_days" in variables) {
        qc.invalidateQueries({ queryKey: qk.kpiReviews(id) });
      }
      // Harmonogram raportów liczy backend z tych trzech pól — bez tego zakładka
      // „Raporty" po przestawieniu trybu dalej twierdziła „raportowanie niewymagane"
      // (i nie pokazywała terminów) aż do przeładowania strony.
      if (variables && ["reporting_mode", "reporting_interval_months", "reporting_start_override"]
        .some((k) => k in variables)) {
        qc.invalidateQueries({ queryKey: qk.reportSchedule(id) });
        qc.invalidateQueries({ queryKey: qk.portfolioReportColumn });
      }
    },
  });
};

// Delete a ZE project from the portfolio (CEO / dyrektor techniczny). Removes the ZE profile only —
// the hub przedsięwzięcie stays. Refresh the portfolio, the connection caches and the nav aggregate.
export const useDeleteConnection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteConnection(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: qk.portfolio });
      qc.invalidateQueries({ queryKey: qk.connection(id) });
      qc.invalidateQueries({ queryKey: qk.byProjectRoot });
    },
  });
};

// Rename the przedsięwzięcie from the workspace header (CEO / dyrektor techniczny). The name lives
// on the hub Project, so refresh the connection (workspace title via by-project prefix) + portfolio.
// Change the przedsięwzięcie's client/branch from the metadata tab (CEO / dyrektor
// techniczny). Client data lives on the hub Project → refresh connection + portfolio.
export const useSetProjectClient = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { client: string | null; client_branch: string | null }) =>
      api.setConnectionClient(id, v),
    onSuccess: () => {
      invalidateConnection(qc, id);
      qc.invalidateQueries({ queryKey: qk.portfolio });
    },
  });
};

export const useRenameProject = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.renameConnectionProject(id, name),
    onSuccess: () => {
      invalidateConnection(qc, id);
      qc.invalidateQueries({ queryKey: qk.portfolio });
    },
  });
};

// Oznacz status tematu (REALIZACJA / PÓŁKA / KOŃCZENIE / ZAMKNIĘTY) — CEO / dyrektor
// techniczny. Badge widać w nagłówku workspace i w portfelu → odśwież oba cache.
export const useSetTopicStatus = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topicStatus: Parameters<typeof api.setTopicStatus>[1]) =>
      api.setTopicStatus(id, topicStatus),
    onSuccess: () => {
      invalidateConnection(qc, id);
      qc.invalidateQueries({ queryKey: qk.portfolio });
    },
  });
};

// Link/unlink the source ZE tender from the metadata tab (CEO / dyrektor techniczny).
// Refresh the connection so the map tab's "Wariant z przetargu ZE" card follows.
export const useSetSourceTender = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceTender: string | null) => api.setSourceTender(id, sourceTender),
    onSuccess: () => invalidateConnection(qc, id),
  });
};

export const useSetMilestone = (connId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      mkey: string;
      state?: string;
      detail?: string;
      due_date?: string | null;
      completed_at?: string | null;
    }) =>
      api.setMilestone(connId, v.mkey, {
        state: v.state,
        detail: v.detail,
        due_date: v.due_date,
        // Absent key = leave the completion date alone (the backend keeps whatever is
        // there); `null` = clear it. Sending `undefined` drops the key from the JSON.
        ...("completed_at" in v ? { completed_at: v.completed_at } : {}),
      }),
    onSuccess: () => invalidateMilestoneGraph(qc, connId),
  });
};

// Add a manual (custom) milestone card to the board.
export const useAddMilestone = (connId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { label: string; state?: string; detail?: string; due_date?: string | null }) =>
      api.addMilestone(connId, v),
    onSuccess: () => invalidateMilestoneGraph(qc, connId),
  });
};

// Delete a custom milestone card (built-in cards are not deletable).
export const useDeleteMilestone = (connId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mkey: string) => api.deleteMilestone(connId, mkey),
    onSuccess: () => invalidateMilestoneGraph(qc, connId),
  });
};

// Drag-drop card order on the milestones board — saved PER USER (the shared default
// order stays untouched). The component holds the new order locally (instant), so we
// only re-sync from the server once the write lands.
export const useReorderMilestones = (connId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mtypes: string[]) => api.reorderMilestones(connId, mtypes),
    onSuccess: () => invalidateConnection(qc, connId),
  });
};

// "Przywróć domyślne" on the milestones board: drops this user's saved order.
export const useResetMilestoneLayout = (connId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resetMilestoneLayout(connId),
    onSuccess: () => invalidateConnection(qc, connId),
  });
};

// ── milestone notes thread (loaded lazily when the details modal opens) ────────
export const useMilestoneNotes = (connId: string, mkey: string, enabled = true) =>
  useQuery({
    queryKey: qk.milestoneNotes(connId, mkey),
    queryFn: () => api.getMilestoneNotes(connId, mkey),
    enabled: enabled && !!connId && !!mkey,
  });

export const useMilestoneNoteMutations = (connId: string, mkey: string) => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.milestoneNotes(connId, mkey) });
  return {
    add: useMutation({
      mutationFn: (v: { text: string; mentionIds?: string[] }) =>
        api.addMilestoneNote(connId, mkey, v.text, v.mentionIds ?? []),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (noteId: string) => api.deleteMilestoneNote(connId, mkey, noteId),
      onSuccess: inv,
    }),
  };
};

// ── async task polling (recompute progress) ──────────────────────────────────
export const useTaskPolling = (taskId: string | null) =>
  useQuery<AsyncTaskRecord>({
    queryKey: qk.task(taskId ?? ""),
    queryFn: () => api.getTask(taskId!),
    enabled: !!taskId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && ["success", "failure", "cancelled"].includes(s) ? false : 1500;
    },
  });

// ── cross-project działka export ("Pobierz listę działek") ─────────────────────
// Cascading: pass the current filters so each list is narrowed by the OTHER filters.
// placeholderData keeps the previous options visible during a refetch (no flicker).
export const useParcelExportFacets = (filters: ParcelExportFilters = {}, enabled = true) =>
  useQuery({
    queryKey: ["pp", "parcel-export-facets", filters],
    queryFn: () => api.getParcelExportFacets(filters),
    enabled,
    placeholderData: (prev) => prev,
  });

// Globalna strona "Działki" — paginowana lista; placeholderData trzyma poprzednią
// stronę podczas dociągania (bez migotania przy zmianie filtra/strony).
export const useGlobalParcels = (req: import("./types").GlobalParcelListRequest) =>
  useQuery({
    queryKey: ["pp", "global-parcels", req],
    queryFn: () => api.getGlobalParcels(req),
    placeholderData: (prev) => prev,
  });

// ── routes ─────────────────────────────────────────────────────────────────────
export const useVariants = (connection: string | undefined) =>
  useQuery({ queryKey: qk.variants(connection ?? ""), queryFn: () => api.getVariants(connection!), enabled: !!connection });

// Unified event timeline (Historia postępów) — pass filters to slice per parcel/office/kind.
export const useProjectEvents = (connection: string | undefined, filters: Record<string, string> = {}) =>
  useQuery({
    queryKey: qk.projectEvents(connection ?? "", filters),
    queryFn: () => api.getProjectEvents({ connection: connection!, ...filters }),
    enabled: !!connection,
  });

// Route course through działki per version (geometry + length).
export const useParcelRouteSegments = (connection: string | undefined, filters: Record<string, string> = {}) =>
  useQuery({
    queryKey: qk.parcelRouteSegments(connection ?? "", filters),
    queryFn: () => api.getParcelRouteSegments({ connection: connection!, ...filters }),
    enabled: !!connection,
  });

export const useCreateVariant = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => api.createVariant(connection, label),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variants(connection) }),
  });
};

// Delete a whole route variant (koordynator projektu / dyrektor techniczny / CEO).
// The cascade removes its versions + their parcel segments and clears route stamps on
// owner-access titles, so invalidate broadly: variants, the timeline, the parcels list
// (route segments / discovered-by / route-review flags) and the connection tiles.
export const useDeleteVariant = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => api.deleteVariant(variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: qk.parcels(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      invalidateConnection(qc, connection);
    },
  });
};

// Choose the binding variant. Recomputes parcel statuses server-side, so invalidate
// as broadly as delete does — statuses, route-review flags and the connection tiles
// all change with the binding route.
export const useSetBindingVariant = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => api.setBindingVariant(variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: qk.parcels(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      invalidateConnection(qc, connection);
    },
  });
};

export const useUploadRouteVersion = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { variant: string; dxf: File; baseMap?: File | null; layerPreset?: string | null; comment?: string }) =>
      api.uploadRouteVersion(v.variant, v.dxf, { baseMap: v.baseMap, layerPreset: v.layerPreset, comment: v.comment }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
    },
  });
};

// Edit a version in place. A DXF/preset swap reparses in the background, which rewrites
// geometry, objects and działki — so invalidate as broadly as the upload path does; the
// caller streams the returned task_id for the live log.
export const useEditRouteVersion = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      versionId: string;
      dxf?: File | null;
      baseMap?: File | null;
      layerPreset?: string | null;
      comment?: string;
    }) =>
      api.editRouteVersion(v.versionId, {
        dxf: v.dxf,
        baseMap: v.baseMap,
        layerPreset: v.layerPreset,
        comment: v.comment,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: qk.parcels(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
    },
  });
};

// Convert a version's DXF between coordinate systems (EPSG). The background reparse
// rewrites geometry, objects and działki — invalidate as broadly as the edit path;
// the caller streams the returned task_id in the "Przetwarzanie" panel.
export const useReprojectRouteVersion = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { versionId: string; sourceSrid: number; targetSrid: number }) =>
      api.reprojectRouteVersion(v.versionId, {
        source_srid: v.sourceSrid,
        target_srid: v.targetSrid,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: qk.parcels(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
    },
  });
};

// Delete one route version. The current version may go (the newest remaining one takes
// over) and the last version removes its variant, so the authoritative route — and with
// it parcel statuses, route-review flags and the connection tiles — can change:
// invalidate as broadly as deleting a variant does.
export const useDeleteRouteVersion = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => api.deleteRouteVersion(versionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.variants(connection) });
      qc.invalidateQueries({ queryKey: qk.parcels(connection) });
      qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      invalidateConnection(qc, connection);
    },
  });
};

// ── DXF layer presets + their layer dictionaries ────────────────────────────────
export const useDxfLayerPresets = () =>
  useQuery({ queryKey: qk.dxfLayerPresets, queryFn: api.getDxfLayerPresets });

export const useDxfPresetMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.dxfLayerPresets });
  return {
    create: useMutation({ mutationFn: api.createDxfLayerPreset, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<DxfLayerPreset> }) => api.updateDxfLayerPreset(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteDxfLayerPreset, onSuccess: inv }),
    duplicate: useMutation({ mutationFn: api.duplicateDxfLayerPreset, onSuccess: inv }),
  };
};

// Layers of one preset. activeOnly=true → only the layers the parser will use
// (for the upload info popover); false → all rows (for the editor).
export const useDxfLayers = (preset: string | undefined, activeOnly = false) =>
  useQuery({
    queryKey: qk.dxfLayers(preset ?? "", activeOnly),
    queryFn: () => api.getDxfLayers({ preset, activeOnly }),
    enabled: !!preset,
  });

export const useDxfLayerMutations = (preset: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.dxfLayersRoot(preset) }); // both activeOnly variants
    qc.invalidateQueries({ queryKey: qk.dxfLayerPresets }); // counts change
  };
  return {
    create: useMutation({ mutationFn: (payload: Partial<DxfLayerCatalogEntry>) => api.createDxfLayer(payload), onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<DxfLayerCatalogEntry> }) => api.updateDxfLayer(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteDxfLayer, onSuccess: inv }),
  };
};

// ── parcels & owners ───────────────────────────────────────────────────────────
export const useParcels = (connection: string | undefined) =>
  useQuery({ queryKey: qk.parcels(connection ?? ""), queryFn: () => api.getParcels(connection!), enabled: !!connection });

// Działki z KONKRETNEJ wersji trasy (trasa w działce albo nasz obiekt na niej).
// Lista działek przyłączenia zbiera warianty razem, więc ekrany działające na
// wybranej wersji (picker do formatek) muszą zawęzić ją tym zbiorem.
export const useRouteVersionParcelIds = (versionId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "route-version-parcels", versionId ?? ""],
    queryFn: () => api.getRouteVersionParcelIds(versionId!),
    enabled: !!versionId,
  });

export const useOwnerMismatches = (connection: string | undefined) =>
  useQuery({
    queryKey: ["power-projects", "owner-mismatches", connection ?? ""],
    queryFn: () => api.getOwnerMismatches(connection!),
    enabled: !!connection,
  });

export const useParcel = (id: string | undefined) =>
  useQuery({ queryKey: qk.parcel(id ?? ""), queryFn: () => api.getParcel(id!), enabled: !!id });

// ── WPW — ścieżka listowna (listy automatyczne) ──────────────────────────────
const wpwMailQk = (params: Record<string, string>) => ["pp", "wpw-mail-letters", params] as const;

export const useWpwMailLetters = (params: Record<string, string>, enabled = true) =>
  useQuery({ queryKey: wpwMailQk(params), queryFn: () => api.getWpwMailLetters(params), enabled });

/** Mutacje listu automatycznego — odświeżają listy listów, kartę działki (właściciel niesie
 * stan listu) i przegląd WPW masowego (kolumna ścieżki listownej). */
export const useWpwMailLetterMutations = (parcelId?: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mail-letters"] });
    qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass"] });
    if (parcelId) qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
  };
  return {
    cancel: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.cancelWpwMailLetter(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    blockOwner: useMutation({
      mutationFn: (v: { ownerId: string; blocked: boolean; reason?: string }) =>
        api.setOwnerWpwMailBlock(v.ownerId, v.blocked, v.reason ?? ""),
      onSuccess: inv,
    }),
    hold: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.holdWpwMailLetter(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    resume: useMutation({ mutationFn: (v: { id: string }) => api.resumeWpwMailLetter(v.id), onSuccess: inv }),
    sendNow: useMutation({ mutationFn: (v: { id: string }) => api.sendWpwMailLetterNow(v.id), onSuccess: inv }),
    decide: useMutation({
      mutationFn: (v: { id: string; outcome: "agreement" | "refusal" | "cancel_attach"; note?: string }) =>
        api.decideWpwMailLetter(v.id, v.outcome, v.note ?? ""),
      onSuccess: inv,
    }),
    blockParcel: useMutation({
      mutationFn: (v: { parcelId: string; blocked: boolean; reason?: string }) =>
        api.setParcelWpwMailBlock(v.parcelId, v.blocked, v.reason ?? ""),
      onSuccess: inv,
    }),
    markOutdated: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.markWpwMailLetterOutdated(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    bulk: useMutation({
      mutationFn: (v: { ids: string[]; action: WpwMailBulkAction; note?: string }) =>
        api.bulkWpwMailLetters(v.ids, v.action, v.note ?? ""),
      onSuccess: inv,
    }),
  };
};

const wpwMailSettingsQk = ["pp", "wpw-mail-settings"] as const;
export const useWpwMailSettings = () =>
  useQuery({ queryKey: wpwMailSettingsQk, queryFn: api.getWpwMailSettings, staleTime: 60_000 });
export const usePatchWpwMailSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { auto_dispatch_days: number }) => api.patchWpwMailSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: wpwMailSettingsQk });
      qc.invalidateQueries({ queryKey: qk.byProjectRoot });
    },
  });
};

// Tabela statusów działki („Dane ogólne"): kolumna na maszynę stanów.
export const useParcelStatusMatrix = (id: string | undefined) =>
  useQuery({
    queryKey: qk.parcelStatusMatrix(id ?? ""),
    queryFn: () => api.getParcelStatusMatrix(id!),
    enabled: !!id,
  });

// Status-change history + the per-owner access titles for the parcel.
export const useParcelHistory = (id: string | undefined) =>
  useQuery({ queryKey: qk.parcelHistory(id ?? ""), queryFn: () => api.getParcelHistory(id!), enabled: !!id });

// Wątek notatek działki — jedna lista dla notatek działki i notatek „per właściciel"
// (te drugie renderują się w niej inaczej, z informacją kogo dotyczą).
export const useParcelNotes = (id: string | undefined) =>
  useQuery({ queryKey: qk.parcelNotes(id ?? ""), queryFn: () => api.getParcelNotes(id!), enabled: !!id });

export const useParcelNoteMutations = (parcelId: string, connectionId?: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.parcelNotes(parcelId) });
    // Notatka ląduje też w „Historii postępów" projektu.
    if (connectionId) qc.invalidateQueries({ queryKey: ["pp", "project-events"] });
  };
  return {
    add: useMutation({
      mutationFn: ({ text, owner }: { text: string; owner?: string | null }) =>
        api.addParcelNote(parcelId, text, owner),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (noteId: string) => api.deleteParcelNote(parcelId, noteId),
      onSuccess: inv,
    }),
  };
};

export const useParcelMutations = (connection: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.parcels(connection) });
    invalidateConnection(qc, connection);
    // Panel „Do obsłużenia" (wnioski) i overview WPW masowego liczą zbiór działek z tych
    // samych danych (trasa / ręczna / ręcznie wykorzystywana, kod własności, właściciele),
    // więc po zmianie kodu własności albo „używana" muszą się przeliczyć bez F5.
    qc.invalidateQueries({ queryKey: qk.appOverview(connection) });
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
  };
  return {
    create: useMutation({ mutationFn: api.createParcel, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Record<string, unknown> }) => api.updateParcel(v.id, v.payload),
      onSuccess: (_p, v) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(v.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    remove: useMutation({ mutationFn: api.deleteParcel, onSuccess: inv }),
    setStatus: useMutation({
      mutationFn: (v: { id: string; status: string; note?: string; basis?: string; secured_at?: string }) =>
        api.setParcelStatus(v.id, v.status, v.note, { basis: v.basis, secured_at: v.secured_at }),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        // ręczne „Uzgodniona" wpisuje tytuły właścicielom → oś czasu projektu też się zmienia
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    fetchUldk: useMutation({
      mutationFn: api.fetchParcelUldk,
      onSuccess: (p) => { inv(); qc.invalidateQueries({ queryKey: qk.parcel(p.id) }); },
    }),
    fetchOwnership: useMutation({
      mutationFn: api.fetchParcelOwnership,
      onSuccess: (p) => { inv(); qc.invalidateQueries({ queryKey: qk.parcel(p.id) }); },
    }),
    // Suggest a street address (does NOT save); the modal saves via `update`.
    suggestStreet: useMutation({ mutationFn: api.suggestParcelStreet }),
    resetStatus: useMutation({
      mutationFn: api.resetParcelStatus,
      onSuccess: (p) => { inv(); qc.invalidateQueries({ queryKey: qk.parcel(p.id) }); },
    }),
    // GK odklikuje, że zmiana trasy na działce nie wymaga działania → gasi badge „do weryfikacji".
    clearRouteReview: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.clearParcelRouteReview(v.id, v.note),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // Porównanie tytułów TEJ działki z aktualną trasą (przebieg + typy, 0,7 m) — działa na
    // wszystkich tytułach działki, bo przebieg jest jeden dla wszystkich jej właścicieli.
    // Przestemplowane tytuły zmieniają status, liczniki i oś czasu jak akcje na tytułach.
    reconcileTitles: useMutation({
      mutationFn: (v: { id: string }) => api.reconcileParcelTitles(v.id),
      onSuccess: (r) => {
        invalidateAccessGraph(qc, connection);
        qc.invalidateQueries({ queryKey: qk.parcel(r.parcel.id) });
      },
    }),
    // „Przelicz wszystkie tytuły": to samo dla każdej działki projektu.
    reconcileAllTitles: useMutation({
      mutationFn: () => api.reconcileConnectionTitles(connection),
      onSuccess: () => invalidateAccessGraph(qc, connection),
    }),
    // Działka podmiotu przyłączanego (inwestora) vs tranzytowa. Przestawia też kategorię
    // uzgodnienia (zegar lokalizacji), więc odświeżamy projekt jak przy trybie pozyskania.
    setConnectedEntity: useMutation({
      mutationFn: (v: { id: string; value: boolean }) => api.setParcelConnectedEntity(v.id, v.value),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // „Potrzebny wypis" mimo wpisanych właścicieli — działka wraca do panelu zamawiania
    // wypisów, więc unieważniamy też jego listę kandydatów.
    setWypisNeeded: useMutation({
      mutationFn: (v: { id: string; value: boolean; reason?: string }) =>
        api.setParcelWypisNeeded(v.id, v.value, v.reason ?? ""),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "wypis-candidates"] });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // Ręczne „wykorzystywana w projekcie" — zmienia filtr listy, postęp projektu i to,
    // co widać na mapie, więc odświeżamy cały projekt jak przy zmianie trybu pozyskania.
    setUsed: useMutation({
      mutationFn: (v: { id: string; value: boolean | null }) => api.setParcelUsed(v.id, v.value),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // Ręczna decyzja o odrolnieniu — zmienia werdykt w tabeli, kreatorze wniosku i na
    // mapie (nakładka), więc odświeżamy projekt jak przy „wykorzystywana".
    setOdrolnienie: useMutation({
      mutationFn: (v: { id: string; value: "required" | "not_required" | null; note?: string }) =>
        api.setParcelOdrolnienie(v.id, v.value, v.note),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // Decyzja koordynatora „droga / nie droga" — przelicza status działki, więc
    // odświeżamy projekt jak przy trybie pozyskania (lista, liczniki, historia).
    setRoad: useMutation({
      mutationFn: (v: { id: string; value: boolean | null; note?: string }) =>
        api.setParcelRoad(v.id, v.value, v.note),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
    // Three-way "what does this parcel need?" — pozyskanie / powiadomienie / nie wymaga.
    setAccessMode: useMutation({
      mutationFn: (v: { id: string; accessMode: ParcelAccessMode }) => api.setParcelAccessMode(v.id, v.accessMode),
      onSuccess: (p) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcel(p.id) });
        qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
      },
    }),
  };
};

// Demolition notifications (zawiadomienie o demontażu): send the per-owner letters into the
// DLC_02 dziennik, and confirm a returned zwrotka ("Uznaj jako dostarczone"). Both refresh the
// proceedings (the notification thread + its items), the parcel and the parcels list (status flips).
export const useNotificationMutations = (connection: string, parcelId: string, letter = false) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "proceedings"] });
    qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
    qc.invalidateQueries({ queryKey: qk.parcels(connection) });
    invalidateConnection(qc, connection);
  };
  return {
    send: useMutation({
      mutationFn: (v: { owners: { id: string; files: File[] }[]; note?: string }) =>
        api.sendDemolitionNotifications(connection, v.owners, v.note ?? "", letter),
      onSuccess: inv,
    }),
    confirmDelivered: useMutation({
      mutationFn: (v: { itemId: string; dateDelivered?: string | null }) =>
        api.confirmNotificationDelivered(v.itemId, v.dateDelivered),
      onSuccess: inv,
    }),
    confirmLetter: useMutation({
      mutationFn: (v: { itemId: string; note?: string }) => api.confirmLetter(v.itemId, v.note ?? ""),
      onSuccess: inv,
    }),
    // Istniejące pismo z Dziennika jako list (pod jednego albo kilku właścicieli) — kandydaci
    // odświeżają się, bo niosą „już podpięte pod…".
    attach: useMutation({
      mutationFn: (v: { ownerIds: string[]; kind: "register" | "print_order"; id: string; note?: string }) =>
        api.attachOwnerLetter(connection, v.ownerIds, { kind: v.kind, id: v.id }, v.note ?? ""),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["pp", "attachable-letters", connection] });
      },
    }),
    detach: useMutation({
      mutationFn: (v: { itemId: string }) => api.detachLetter(v.itemId),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["pp", "attachable-letters", connection] });
      },
    }),
    // Wynik listu (uzgodnienie / odmowa) zmienia tytuł właściciela → działka i lista.
    // `files` = opcjonalne pismo z uzgodnieniem / odmową (ląduje na tytule).
    setOutcome: useMutation({
      mutationFn: (v: { itemId: string; outcome: "agreement" | "refusal"; note?: string; files?: File[] }) =>
        api.setLetterOutcome(v.itemId, v.outcome, v.note ?? "", v.files ?? []),
      onSuccess: inv,
    }),
  };
};

/** Pisma wychodzące z Dziennika (DLC_02) do podpięcia pod właściciela — dociągane dopiero,
 * gdy użytkownik otworzy wybór (enabled), żeby panel listów nie pytał Dziennika bez potrzeby. */
export const useAttachableLetters = (connection: string, enabled: boolean) =>
  useQuery({
    queryKey: ["pp", "attachable-letters", connection] as const,
    queryFn: () => api.getAttachableLetters(connection),
    enabled,
  });

// Link/unlink WPW (terenowe/kompleksowe) orders to a działka; both refresh the parcel
// (status flips on WPW link/unlink — "w terenie") and the parcels list.
export const useOrderLinkMutations = (parcelId: string, connection: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
    qc.invalidateQueries({ queryKey: qk.parcels(connection) });
  };
  return {
    link: useMutation({
      mutationFn: (v: { order: string; scopeNote?: string }) => api.linkOrderParcel(v.order, parcelId, v.scopeNote ?? ""),
      onSuccess: inv,
    }),
    unlink: useMutation({ mutationFn: (linkId: string) => api.unlinkOrderParcel(linkId), onSuccess: inv }),
  };
};

export const useLinkableOrders = (params: Record<string, string>, enabled = true) =>
  useQuery({
    queryKey: qk.linkableOrders(params),
    queryFn: () => api.getLinkableOrders(params),
    enabled,
  });

export const useOwnerMutations = (parcelId: string, connection: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
    qc.invalidateQueries({ queryKey: qk.parcels(connection) });
  };
  return {
    create: useMutation({ mutationFn: api.createOwner, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Record<string, unknown> }) => api.updateOwner(v.id, v.payload),
      onSuccess: inv,
    }),
    // Optimistic delete: drop the owner from both caches immediately so the card disappears
    // "od razu", then reconcile via inv() on settle. getParcel is a heavy refetch (per-owner WPW
    // + access-title lookups), so waiting for it makes the row linger and read as "nie znika".
    remove: useMutation({
      mutationFn: api.deleteOwner,
      onMutate: async (ownerId: string) => {
        await qc.cancelQueries({ queryKey: qk.parcel(parcelId) });
        await qc.cancelQueries({ queryKey: qk.parcels(connection) });
        const prevParcel = qc.getQueryData<Parcel>(qk.parcel(parcelId));
        const prevParcels = qc.getQueryData<Parcel[]>(qk.parcels(connection));
        const dropOwner = (p: Parcel): Parcel => {
          const owners = p.owners.filter((o) => o.id !== ownerId);
          const owners_total = Math.max(0, p.owners_total - 1);
          return { ...p, owners, owners_total, owners_agreed: Math.min(p.owners_agreed, owners_total) };
        };
        if (prevParcel) qc.setQueryData<Parcel>(qk.parcel(parcelId), dropOwner(prevParcel));
        if (prevParcels)
          qc.setQueryData<Parcel[]>(qk.parcels(connection), prevParcels.map((p) => (p.id === parcelId ? dropOwner(p) : p)));
        return { prevParcel, prevParcels };
      },
      // Delete failed — restore both caches to the pre-drop snapshot.
      onError: (_err, _ownerId, ctx) => {
        if (ctx?.prevParcel) qc.setQueryData(qk.parcel(parcelId), ctx.prevParcel);
        if (ctx?.prevParcels) qc.setQueryData(qk.parcels(connection), ctx.prevParcels);
      },
      onSettled: inv,
    }),
    uploadWypis: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadWypis(v.id, v.file),
      onSuccess: inv,
    }),
    // Ręczna poprawka małżeństwa z wypisu (łączenie w jeden podpis / rozłączanie).
    joinMarital: useMutation({
      mutationFn: (v: { id: string; spouseId: string }) => api.joinOwnerMarital(v.id, v.spouseId),
      onSuccess: inv,
    }),
    leaveMarital: useMutation({ mutationFn: api.leaveOwnerMarital, onSuccess: inv }),
  };
};

// ── access basis registry (rozszerzalny rejestr podstaw) ──────────────────────────
export const useAccessBasisTypes = (activeOnly = false) =>
  useQuery({ queryKey: qk.accessBasisTypes(activeOnly), queryFn: () => api.getAccessBasisTypes(activeOnly) });

export const useAccessBasisTypeMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["pp", "access-basis-types"] });
  return {
    create: useMutation({ mutationFn: api.createAccessBasisType, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<AccessBasisType> }) => api.updateAccessBasisType(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteAccessBasisType, onSuccess: inv }),
  };
};

// ── per-owner access titles (OwnerAccess) ────────────────────────────────────────
// Slice by owner / parcel / connection / status (filters passed straight through).
export const useOwnerAccesses = (filters: Record<string, string>, enabled = true) =>
  useQuery({
    queryKey: qk.ownerAccesses(filters),
    queryFn: () => api.getOwnerAccesses(filters),
    enabled,
  });

export const useOwnerAccessEvents = (id: string | undefined) =>
  useQuery({
    queryKey: qk.ownerAccessEvents(id ?? ""),
    queryFn: () => api.getOwnerAccessEvents(id!),
    enabled: !!id,
  });

// All per-owner title actions feed the parcel auto-status, so they invalidate the
// whole access graph for the connection.
export const useOwnerAccessMutations = (connection: string) => {
  const qc = useQueryClient();
  const inv = () => invalidateAccessGraph(qc, connection);
  return {
    create: useMutation({
      mutationFn: (v: { ownerId: string; payload?: Partial<OwnerAccess> }) => api.createOwnerAccess(v.ownerId, v.payload),
      onSuccess: inv,
    }),
    transition: useMutation({
      mutationFn: (v: { id: string } & Parameters<typeof api.transitionOwnerAccess>[1]) =>
        api.transitionOwnerAccess(v.id, v),
      onSuccess: inv,
    }),
    secure: useMutation({
      mutationFn: (v: { id: string } & Parameters<typeof api.secureOwnerAccess>[1]) =>
        api.secureOwnerAccess(v.id, v),
      onSuccess: inv,
    }),
    notRequired: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.ownerAccessNotRequired(v.id, v.note),
      onSuccess: inv,
    }),
    reportProblem: useMutation({
      mutationFn: (v: { id: string; note: string }) => api.reportOwnerAccessProblem(v.id, v.note),
      onSuccess: inv,
    }),
    extendRoute: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.extendOwnerAccessRoute(v.id, v.note),
      onSuccess: inv,
    }),
    uploadFiles: useMutation({
      mutationFn: (v: { id: string; files: File[] }) => api.uploadOwnerAccessFiles(v.id, v.files),
      onSuccess: inv,
    }),
    removeFile: useMutation({
      mutationFn: (v: { id: string; fileId: string }) => api.removeOwnerAccessFile(v.id, v.fileId),
      onSuccess: inv,
    }),
  };
};

// ── proceedings (universal correspondence thread) ────────────────────────────────
// Slice by connection/scope/status/type/office/owner/parcel/owner_parcel/search.
// Sprawy to obiekty OBIEGU (odpowiedź urzędu, zamknięcie, nowa pozycja robią się
// poza tą kartą, a dzwonek PROCEEDING_RESPONSE prowadzi prosto tutaj) — bez
// staleTime:0 klik w powiadomienie pokazywał stan z cache sprzed zdarzenia
// (ten sam mechanizm co incydent wniosków z 2026-09-29).
// keepPrevious: lista z filtrami na żywo zostaje na ekranie do czasu nowej odpowiedzi.
export const useProceedings = (
  filters: Record<string, string>,
  enabled = true,
  { keepPrevious = false }: { keepPrevious?: boolean } = {},
) =>
  useQuery({
    queryKey: qk.proceedings(filters),
    queryFn: () => api.getProceedings(filters),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });

export const useProceeding = (id: string | undefined) =>
  useQuery({
    queryKey: qk.proceeding(id ?? ""),
    queryFn: () => api.getProceeding(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useProceedingTypes = (activeOnly = false) =>
  useQuery({ queryKey: qk.proceedingTypes(activeOnly), queryFn: () => api.getProceedingTypes(activeOnly) });

// Closing/transitioning a thread (OFFICE → OwnerAccess) can change parcel statuses, so
// proceeding mutations invalidate the whole access graph + the proceeding-types registry.
export const useProceedingMutations = (connection: string) => {
  const qc = useQueryClient();
  const inv = () => invalidateAccessGraph(qc, connection);
  // Wiązanie zmienia też listę kandydatów (co jeszcze da się podpiąć).
  const invLinks = () => {
    inv();
    qc.invalidateQueries({ queryKey: ["pp", "proceeding-link-candidates"] });
  };
  return {
    create: useMutation({ mutationFn: api.createProceeding, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<Proceeding> & Record<string, unknown> }) =>
        api.updateProceeding(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteProceeding, onSuccess: inv }),
    // Sprawa w kalendarzu ponagleń (włącz = dzwonię ja / wyłącz).
    setControlCalls: useMutation({
      mutationFn: (v: { id: string; enabled: boolean }) =>
        api.setProceedingControlCalls(v.id, v.enabled),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["pp", "control-calls"] });
      },
    }),
    close: useMutation({
      mutationFn: ({ id, ...values }: { id: string } & CaseCloseValues) =>
        api.closeProceeding(id, values),
      onSuccess: inv,
    }),
    reopen: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.reopenProceeding(v.id, v.note),
      onSuccess: inv,
    }),
    linkOrder: useMutation({
      mutationFn: (v: { id: string; order: string }) => api.linkProceedingOrder(v.id, v.order),
      onSuccess: invLinks,
    }),
    unlinkOrder: useMutation({
      mutationFn: (v: { id: string; order: string }) => api.unlinkProceedingOrder(v.id, v.order),
      onSuccess: invLinks,
    }),
    linkApplication: useMutation({
      mutationFn: (v: {
        id: string;
        application: string;
        close?: CaseCloseValues;
        reopen?: boolean;
      }) => api.linkProceedingApplication(v.id, v.application, v.close, v.reopen),
      onSuccess: invLinks,
    }),
    assignPackageItem: useMutation({
      mutationFn: (v: { id: string; application: string; item: string | null }) =>
        api.assignProceedingPackageItem(v.id, v.application, v.item),
      // Strona pakietu pokazuje swoją pozycję w sprawie.
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["pp", "application"] });
      },
    }),
    unlinkApplication: useMutation({
      mutationFn: (v: { id: string; application: string; deleteCase?: boolean }) =>
        api.unlinkProceedingApplication(v.id, v.application, v.deleteCase),
      // Pakiet (jego strona „Sprawa w Korespondencji") też się zmienia.
      onSuccess: () => {
        invLinks();
        qc.invalidateQueries({ queryKey: ["pp", "applications"] });
        qc.invalidateQueries({ queryKey: ["pp", "application"] });
      },
    }),
    markUzupelnienie: useMutation({
      mutationFn: (v: { id: string; note?: string }) =>
        api.markProceedingUzupelnienie(v.id, v.note),
      onSuccess: inv,
    }),
    // Items (multifile) — the caller builds the FormData.
    addApplication: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.addApplication(v.id, v.fd),
      onSuccess: inv,
    }),
    addResponse: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.addResponse(v.id, v.fd),
      onSuccess: inv,
    }),
    addEvent: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.addEvent(v.id, v.fd),
      onSuccess: inv,
    }),
    // Skan odpowiedzi → dokument tytułów sprawy (karta właściciela, foldery w Plikach).
    setAsTitle: useMutation({
      mutationFn: (itemId: string) => api.setResponseAsTitle(itemId),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: qk.projectFolders(connection) });
      },
    }),
    // Application lifecycle (gated by per-project can_approve / can_submit).
    approve: useMutation({ mutationFn: (itemId: string) => api.approveApplication(itemId), onSuccess: inv }),
    submit: useMutation({
      mutationFn: (v: { itemId: string; dateSent?: string | null }) => api.submitApplication(v.itemId, v.dateSent),
      onSuccess: inv,
    }),
    updateItem: useMutation({
      mutationFn: (v: { itemId: string; payload: api.ItemEditPayload }) =>
        api.updateProceedingItem(v.itemId, v.payload),
      onSuccess: inv,
    }),
    deleteItem: useMutation({
      mutationFn: (itemId: string) => api.deleteProceedingItem(itemId),
      onSuccess: inv,
    }),
    // Attachments of an existing item (same gate as updateItem).
    uploadItemFiles: useMutation({
      mutationFn: (v: { itemId: string; files: File[] }) =>
        api.uploadProceedingItemFiles(v.itemId, v.files),
      onSuccess: inv,
    }),
    deleteItemFile: useMutation({
      mutationFn: (v: { itemId: string; fileId: string }) =>
        api.deleteProceedingItemFile(v.itemId, v.fileId),
      onSuccess: inv,
    }),
    // "Inny (wpisz opis)" type flow — idempotent create.
    createType: useMutation({
      mutationFn: (name: string) => api.createProceedingType(name),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "proceeding-types"] }),
    }),
    // Rename a type — director-only (gated on the backend); refresh the registry on success.
    updateType: useMutation({
      mutationFn: (v: { id: string; name: string }) => api.updateProceedingType(v.id, v.name),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "proceeding-types"] }),
    }),
  };
};

// ── per-project access permissions (panel "Metadane", director) ────────────────────
export const useProjectPermissions = (connection: string | undefined) =>
  useQuery({
    queryKey: qk.projectPermissions(connection ?? ""),
    queryFn: () => api.getProjectPermissions(connection!),
    enabled: !!connection,
  });

// Effective approve/submit gate for the current user on this project — the frontend
// mirror of backend `_can` (services.py): director/CEO and the przedsięwzięcie's
// coordinators hold both implicitly (assigning a coordinator IS the grant); everyone
// else needs an explicit ProjectAccessPermission row from panel "Metadane".
export const useMyProjectGate = (connection: string | undefined) => {
  const user = useAuthStore((s) => s.user);
  const { data: conn } = useConnection(connection);
  const { data: permissions } = useProjectPermissions(connection);
  return useMemo(() => {
    const implicit =
      !!user &&
      (user.role === "director" || !!user.is_ceo ||
        (conn?.project_coordinators ?? []).includes(user.id));
    const mine = (permissions ?? []).find((p) => p.user === user?.id);
    return {
      canSubmit: implicit || !!mine?.can_submit,
      canApprove: implicit || !!mine?.can_approve,
    };
  }, [user, conn, permissions]);
};

// Contractors + their framework line items — for the WPW requisition modal.
export const useActiveContractors = (enabled = true, eligibleFor?: "wpw") =>
  useQuery({
    queryKey: ["pp", "contractors-active", eligibleFor ?? ""],
    queryFn: () => api.getActiveContractors(eligibleFor),
    enabled,
  });

// Pozycje automatyczne umowy ramowej dla formularza zapotrzebowania (z werdyktem
// „doda się / nie doda się i dlaczego” dla tego projektu).
export const useAutoLineItemsPreview = (contractorId: string | undefined, connectionId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "auto-line-items", contractorId ?? "", connectionId ?? ""],
    queryFn: () => api.getAutoLineItemsPreview(contractorId!, { connection: connectionId }),
    enabled: !!contractorId && !!connectionId,
  });

export const useContractorAgreements = (contractorId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "contractor-agreements", contractorId ?? ""],
    queryFn: () => api.getContractorAgreements(contractorId!),
    enabled: !!contractorId,
  });

// Design (PT/PAB/PZT) contractor hard filter (+ workload counts) + checkpoint template.
export const useDesignContractors = (
  params: { docType?: string; coordinator?: string; connection?: string; all?: boolean },
  enabled = true,
) =>
  useQuery({
    queryKey: [
      "pp", "design-contractors",
      params.docType ?? "", params.coordinator ?? "", params.connection ?? "", params.all ? "all" : "",
    ],
    queryFn: () => api.getDesignContractors(params),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useTechnicalSubcontractors = (enabled = true) =>
  useQuery({
    queryKey: ["pp", "advisory-technical-subcontractors"],
    queryFn: () => api.getTechnicalSubcontractors(),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useCheckpointTemplate = (path = "technical", enabled = true) =>
  useQuery({
    queryKey: ["pp", "checkpoint-template", path],
    queryFn: () => api.getCheckpointTemplate(path),
    enabled,
    staleTime: 60 * 60_000,
  });

// Raise a Requisition for private-owner consents (WPW) from the Działki tab.
export const useCreateWpwRequisition = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fd: FormData) => api.createWpwRequisition(connection, fd),
    onSuccess: () => invalidateAccessGraph(qc, connection),
  });
};

export const useProjectPermissionMutations = (connection: string) => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.projectPermissions(connection) });
  return {
    set: useMutation({
      mutationFn: (v: { user: string; can_approve: boolean; can_submit: boolean }) =>
        api.setProjectPermission({ connection, ...v }),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteProjectPermission(id), onSuccess: inv }),
  };
};

// ── KPI reviews ─────────────────────────────────────────────────────────────
export const useKpiReviews = (connection: string | undefined) =>
  useQuery({ queryKey: qk.kpiReviews(connection ?? ""), queryFn: () => api.getKpiReviews(connection!), enabled: !!connection });

export const useKpiReviewMutations = (connection: string) => {
  const qc = useQueryClient();
  // create/close/cancel log a KPI timeline event and (on close) shift progress — refresh
  // the reviews, the Historia postępów feed and the connection (progress tiles).
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.kpiReviews(connection) });
    qc.invalidateQueries({ queryKey: ["pp", "project-events", connection] });
    invalidateConnection(qc, connection);
  };
  return {
    create: useMutation({ mutationFn: api.createKpiReview, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<KpiReview> }) => api.updateKpiReview(v.id, v.payload),
      onSuccess: inv,
    }),
    close: useMutation({
      mutationFn: (v: { id: string; summary?: string }) => api.closeKpiReview(v.id, v.summary),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelKpiReview(id), onSuccess: inv }),
  };
};

export const useKpiActionItemMutations = (connection: string) => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.kpiReviews(connection) });
  return {
    create: useMutation({ mutationFn: api.createKpiActionItem, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<KpiActionItem> }) => api.updateKpiActionItem(v.id, v.payload),
      onSuccess: inv,
    }),
    setStatus: useMutation({
      mutationFn: (v: { id: string; status: string }) => api.setKpiActionItemStatus(v.id, v.status),
      onSuccess: inv,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) => api.reorderKpiActionItems(connection, ids),
      onSuccess: inv,
    }),
    reparent: useMutation({
      mutationFn: (v: { id: string; parent?: string | null; due_date?: string | null }) => {
        const { id, ...payload } = v;
        return api.reparentKpiActionItem(id, payload);
      },
      onSuccess: inv,
    }),
    carryOver: useMutation({
      mutationFn: (week?: string) => api.carryOverKpiActionItems(connection, week),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteKpiActionItem, onSuccess: inv }),
    // Dzwoneczek: po ponagleniu odświeżamy drzewo (licznik pingów w wierszu) i historię
    // pod kartą zadania.
    ping: useMutation({
      mutationFn: api.pingKpiActionItem,
      onSuccess: (_data, id) => {
        inv();
        qc.invalidateQueries({ queryKey: qk.kpiItemPings(id) });
      },
    }),
  };
};

/** Historia ponagleń jednego zadania — czytana dopiero po otwarciu karty zadania. */
export const useKpiItemPings = (itemId: string | undefined) =>
  useQuery({
    queryKey: qk.kpiItemPings(itemId ?? ""),
    queryFn: () => api.getKpiItemPings(itemId!),
    enabled: !!itemId,
  });

// Per-user collapse of the month/week groups. Read once per project; writes are optimistic
// so folding a month never waits on the network.
export const useKpiGroupStates = (connection: string | undefined) => {
  const qc = useQueryClient();
  const key = ["pp", "kpi-group-states", connection ?? ""];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getKpiGroupStates(connection!),
    enabled: !!connection,
    staleTime: 5 * 60 * 1000,
  });
  const toggle = useMutation({
    mutationFn: (v: { groupKey: string; collapsed: boolean }) =>
      api.setKpiGroupCollapsed(connection!, v.groupKey, v.collapsed),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ collapsed: string[] }>(key);
      const collapsed = new Set(prev?.collapsed ?? []);
      if (v.collapsed) collapsed.add(v.groupKey);
      else collapsed.delete(v.groupKey);
      qc.setQueryData(key, { collapsed: [...collapsed] });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
  });
  return { collapsed: new Set(query.data?.collapsed ?? []), toggle };
};

// ── globalna strona "Zadania KPI" ───────────────────────────────────────────────
// Cascading facets (przekaż bieżące filtry, żeby każda lista zawęziła się POZOSTAŁYMI)
// + paginowana lista. placeholderData trzyma poprzednią stronę podczas dociągania.
export const useGlobalKpiFacets = (filters: GlobalKpiFilters = {}, enabled = true) =>
  useQuery({
    queryKey: ["pp", "global-kpi-facets", filters],
    queryFn: () => api.getGlobalKpiFacets(filters),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useGlobalKpiTasks = (req: GlobalKpiListRequest) =>
  useQuery({
    queryKey: ["pp", "global-kpi-list", req],
    queryFn: () => api.getGlobalKpiTasks(req),
    placeholderData: (prev) => prev,
  });

// Odhaczenie / cofnięcie zadania prosto z listy globalnej — odświeża listę i kafelki.
export const useGlobalKpiStatusMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: string }) => api.setKpiActionItemStatus(v.id, v.status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "global-kpi-list"] });
      qc.invalidateQueries({ queryKey: ["pp", "global-kpi-facets"] });
    },
  });
};

// ── „Analiza zmian" — rozliczenie osoby względem wybranej chwili ───────────────
export const useChangeAnalysisPeople = (enabled = true) =>
  useQuery({
    queryKey: ["pp", "change-analysis-people"],
    queryFn: api.getChangeAnalysisPeople,
    enabled,
  });

export const useChangeAnalysis = (params: ChangeAnalysisRequest | null) =>
  useQuery({
    queryKey: ["pp", "change-analysis", params],
    queryFn: () => api.getChangeAnalysis(params!),
    enabled: !!params,
  });

// ── KPI discussion (comments under a review) ─────────────────────────────────────
export const useKpiComments = (reviewId: string | undefined) =>
  useQuery({
    queryKey: qk.kpiComments(reviewId ?? ""),
    queryFn: () => api.getKpiComments(reviewId!),
    enabled: !!reviewId,
  });

export const useKpiCommentMutations = (reviewId: string, connection: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.kpiComments(reviewId) });
    qc.invalidateQueries({ queryKey: qk.kpiReviews(connection) }); // refresh comments_count
  };
  return {
    create: useMutation({
      mutationFn: (v: { content: string; mentionIds?: string[] }) =>
        api.createKpiComment(reviewId, v.content, v.mentionIds ?? []),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; content: string; mentionIds?: string[] }) =>
        api.updateKpiComment(v.id, v.content, v.mentionIds ?? []),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteKpiComment(id), onSuccess: inv }),
  };
};

// Komentarze pod pojedynczym zadaniem KPI. Odświeżają też listę przeglądów, bo to stamtąd
// drzewo bierze licznik 💬 przy zadaniu.
export const useKpiItemComments = (itemId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "kpi-item-comments", itemId ?? ""],
    queryFn: () => api.getKpiItemComments(itemId!),
    enabled: !!itemId,
  });

export const useKpiItemCommentMutations = (itemId: string, connection: string) => {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "kpi-item-comments", itemId] });
    qc.invalidateQueries({ queryKey: qk.kpiReviews(connection) });
  };
  return {
    create: useMutation({
      mutationFn: (v: { content: string; mentionIds?: string[] }) =>
        api.createKpiItemComment(itemId, v.content, v.mentionIds ?? []),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; content: string; mentionIds?: string[] }) =>
        api.updateKpiItemComment(v.id, v.content, v.mentionIds ?? []),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteKpiItemComment(id), onSuccess: inv }),
  };
};

// ── offices ──────────────────────────────────────────────────────────────────
export const useOffices = () => useQuery({ queryKey: qk.offices, queryFn: api.getOffices });
export const useOfficeMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.offices });
  // Poprawka urzędu przepisuje też MIGAWKI nazwy i adresu na właścicielach działek
  // (backend, resync_office_snapshots) — otwarta karta działki pokazywałaby bez tego
  // stary adres, czyli dokładnie ten, dla którego poprawka powstała.
  const invParcels = () => {
    inv();
    qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
  };
  return {
    create: useMutation({ mutationFn: api.createOffice, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<Office> }) => api.updateOffice(v.id, v.payload),
      onSuccess: invParcels,
    }),
    remove: useMutation({ mutationFn: api.deleteOffice, onSuccess: invParcels }),
    merge: useMutation({
      mutationFn: (v: { id: string; duplicates: string[]; dryRun?: boolean }) =>
        api.mergeOffices(v.id, v.duplicates, v.dryRun),
      onSuccess: (r) => { if (!r.dry_run) invParcels(); },
    }),
  };
};

// Lista urzędów DO WYBORU — bez wpisów wycofanych (is_active=false). Bez tego filtra
// dezaktywacja byłaby pustym gestem: wycofany urząd dalej podpowiadałby się wszędzie, więc
// jedynym sposobem „usunięcia go z obiegu" pozostawałoby zakładanie duplikatu.
// `keep` przypina wpis JUŻ wybrany w danym formularzu, żeby wycofanie urzędu nie wyczyściło
// pola w rekordzie, który go wskazuje. Rejestr i scalanie duplikatów używają useOffices().
export const useSelectableOffices = (keep?: string | null) => {
  const q = useOffices();
  const data = useMemo(
    () => (q.data ?? []).filter((o) => o.is_active || (!!keep && o.id === keep)),
    [q.data, keep],
  );
  return { ...q, data };
};

// Gdzie dany urząd jest używany — pobierane dopiero, gdy formularz edycji jest otwarty.
export const useOfficeUsage = (id: string | null) =>
  useQuery({
    queryKey: ["pp", "office-usage", id],
    queryFn: () => api.getOfficeUsage(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

// ── metadata presets ─────────────────────────────────────────────────────────
export const usePresets = () => useQuery({ queryKey: qk.presets, queryFn: api.getPresets });
export const usePresetMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.presets });
  return {
    create: useMutation({
      mutationFn: (v: { name: string; columns: string[] }) => api.createPreset(v.name, v.columns),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; name: string; columns: string[] }) => api.updatePreset(v.id, v.name, v.columns),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deletePreset, onSuccess: inv }),
  };
};

// ── słownik projektantów uprawnionych ──────────────────────────────────────────
export const useDesigners = () => useQuery({ queryKey: qk.designers, queryFn: api.getDesigners });
export const useDesignerMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.designers });
  return {
    create: useMutation({ mutationFn: (v: DesignerInput) => api.createDesigner(v), onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, ...rest }: { id: string } & Partial<DesignerInput>) => api.updateDesigner(id, rest),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteDesigner, onSuccess: inv }),
    uploadSignature: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadDesignerSignature(v.id, v.file),
      onSuccess: inv,
    }),
  };
};

// ── książka telefoniczna (kontakty z projektów ZE) ─────────────────────────────
export const useProjectContacts = (search?: string) =>
  useQuery({
    queryKey: ["pp", "contacts", search ?? ""] as const,
    queryFn: () => api.getProjectContacts(search),
  });

export const useProjectContactProjects = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ["pp", "contact-projects", id ?? ""] as const,
    queryFn: () => api.getProjectContactProjects(id!),
    enabled: enabled && !!id,
  });

export const useProjectContactMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["pp", "contacts"] });
  return {
    create: useMutation({
      mutationFn: (v: Parameters<typeof api.createProjectContact>[0]) => api.createProjectContact(v),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.updateProjectContact>[1] }) =>
        api.updateProjectContact(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteProjectContact(id), onSuccess: inv }),
  };
};

// ── DLC_02 — firmowy dziennik korespondencji (CorrespondenceRegister) ─────────────
export const useCorrespondenceRegister = (params?: Record<string, string>) =>
  useQuery({
    queryKey: qk.register(params),
    queryFn: () => api.getCorrespondenceRegister(params),
    // Rozpoznawanie QR wpływu biegnie w tle (Celery) — odpytuj listę, dopóki
    // któryś wiersz jest w toku, żeby sugestia dekretacji pojawiła się sama.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.qr_scan_status === "pending") ? 4000 : false,
  });

// Badge counts (do odebrania / do zadekretowania). Shares the register cache prefix, so the
// register mutations' invalidation refreshes it automatically.
export const useRegisterCounts = (enabled = true) =>
  useQuery({
    queryKey: ["pp", "correspondence-register", "counts"] as const,
    queryFn: () => api.getRegisterCounts(),
    enabled,
  });

// Słownik przedsięwzięć dziennika (id + nazwa + koordynatorzy) — dostępny dla każdego
// użytkownika DLC_02, w odróżnieniu od usePortfolio (wymaga modułu DLC_01).
export const useRegisterProjectOptions = () =>
  useQuery({
    queryKey: ["pp", "register-project-options"] as const,
    queryFn: () => api.getRegisterProjectOptions(),
  });

// Podpowiedzi korespondentów do filtra nagłówka. Świadomie NIE zawężane pozostałymi
// filtrami — lista ma pokazywać, kto w ogóle jest w dzienniku, a nie topnieć po wybraniu
// kierunku. Dzieli prefiks cache'u z rejestrem, więc mutacje odświeżają ją same.
export const useRegisterCounterpartyOptions = (params?: Record<string, string>) =>
  useQuery({
    queryKey: ["pp", "correspondence-register", "counterparty-options", params ?? {}] as const,
    queryFn: () => api.getRegisterCounterpartyOptions(params),
  });

// Invalidation clears all register caches regardless of the active list filter.
export const useRegisterMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["pp", "correspondence-register"] });
  return {
    // Manual outgoing intake (FormData: text fields + repeated `files`).
    createOutgoing: useMutation({ mutationFn: (fd: FormData) => api.createOutgoingRegister(fd), onSuccess: inv }),
    // Rejestracja wpływu (FormData: text fields + repeated `files`).
    registerIncoming: useMutation({ mutationFn: (fd: FormData) => api.registerIncoming(fd), onSuccess: inv }),
    // Edycja niezadekretowanego wpływu (FormData: pełny stan formularza + remove_files).
    updateIncoming: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.updateIncomingRegister(v.id, v.fd),
      onSuccess: inv,
    }),
    // Podział wpływu na pozycje — rozbij niezadekretowany wpływ na 2+ nowe pozycje rejestru.
    splitIncoming: useMutation({
      mutationFn: (v: {
        id: string;
        parts: { files: string[]; target_users: string[]; target_project?: string | null; note?: string }[];
      }) => api.splitIncomingRegister(v.id, v.parts),
      onSuccess: inv,
    }),
    // Dispatch lifecycle (FormData may carry a confirmation_file).
    transition: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.transitionRegister(v.id, v.fd),
      onSuccess: inv,
    }),
    // Nr listu dopisany po wysłaniu pisma — dział obiegu dokumentów.
    setTrackingNumber: useMutation({
      mutationFn: (v: { id: string; trackingNumber: string }) =>
        api.setRegisterTrackingNumber(v.id, v.trackingNumber),
      onSuccess: inv,
    }),
    // Dekretacja — route incoming to person/project, mark FILED.
    fileIncoming: useMutation({
      mutationFn: (v: { id: string; target_users?: string[]; target_project?: string | null; note?: string }) =>
        api.fileIncomingRegister(v.id, { target_users: v.target_users, target_project: v.target_project, note: v.note }),
      onSuccess: inv,
    }),
    // Anulowanie własnego pisma w kolejce wysyłki — autor wpisu wychodzącego („Moje pisma
    // do wysłania"), dopóki pismo nie wyszło.
    cancelOutgoing: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.cancelOutgoingRegister(v.id, v.note),
      onSuccess: inv,
    }),
    // Wycofanie przekazania firmie prowadzącej — dział obiegu albo adresat dekretacji.
    withdrawFromContractor: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        api.withdrawRegisterFromContractor(v.id, v.reason),
      onSuccess: inv,
    }),
    // Podpięcie projektu — autor wpisu / adresat dekretacji / dział obiegu.
    assignProject: useMutation({
      mutationFn: (v: { id: string; target_project: string }) =>
        api.assignRegisterProject(v.id, v.target_project),
      onSuccess: inv,
    }),
    // Odpięcie projektu — tylko adresat dekretacji albo dyrektor; lustrzane pliki znikają
    // z zakładki Pliki przedsięwzięcia.
    unassignProject: useMutation({
      mutationFn: (id: string) => api.unassignRegisterProject(id),
      onSuccess: inv,
    }),
    // Potwierdzenie odbioru — the dekretacja target confirms receipt (FILED → ACKNOWLEDGED).
    acknowledge: useMutation({
      mutationFn: (id: string) => api.acknowledgeRegister(id),
      onSuccess: inv,
    }),
    // Dodanie adresata po dekretacji — obecny adresat albo dyrektor / dział obiegu.
    addRecipients: useMutation({
      mutationFn: (v: { id: string; target_users: string[] }) =>
        api.addRegisterRecipients(v.id, v.target_users),
      onSuccess: inv,
    }),
    // Usunięcie adresata — tylko dyrektor / dział obiegu dokumentów.
    removeRecipients: useMutation({
      mutationFn: (v: { id: string; target_users: string[] }) =>
        api.removeRegisterRecipients(v.id, v.target_users),
      onSuccess: inv,
    }),
    // Pobierz zaznaczone załączniki (z wielu wpisów) jako jeden ZIP — nie zmienia stanu, brak invalidacji.
    downloadZip: useMutation({ mutationFn: (fileIds: string[]) => api.downloadRegisterZip(fileIds) }),
  };
};

// ── WPW handover (działka | WPW) ──────────────────────────────────────────────────
export function useWpwHandovers(params: Record<string, string>, enabled = true) {
  return useQuery({
    queryKey: qk.wpwHandovers(params),
    queryFn: () => api.getWpwHandovers(params),
    enabled,
    // Przekazania zmienia druga strona (negocjator deklaruje status, GK potwierdza),
    // a dzwonek WPW_AWAITING_CONFIRMATION prowadzi tutaj — zawsze świeży stan.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useWpwNeverSentParcels(connectionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.wpwNeverSent(connectionId ?? ""),
    queryFn: () => api.getWpwNeverSentParcels(connectionId as string),
    enabled: enabled && !!connectionId,
  });
}

// Per-tab "needs attention" counts for the workspace badges (poll so badges stay fresh).
export function useConnectionAttention(connectionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.attention(connectionId ?? ""),
    queryFn: () => api.getConnectionAttention(connectionId as string),
    enabled: enabled && !!connectionId,
    refetchInterval: 60_000,
  });
}

// Toggle the per-user mute of a project's badges; refreshes the project's attention counts.
export function useBadgeMuteMutation(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (muted: boolean) => api.setConnectionBadgeMute(connectionId, muted),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.attention(connectionId) });
    },
  });
}

export const useWpwHandoverMutations = () => {
  const qc = useQueryClient();
  // A WPW change flips the owner's declared state, which feeds BOTH the handover lists AND the
  // parcel auto-status (a started handover → owner "in field", a confirmed acquisition → SECURED
  // title). So refresh the handover lists (broadly — the panel is keyed by {order} on the order
  // page and by {connection} in the workspace, so a connection-scoped key would miss the order
  // view) AND the whole access graph for the affected connection, exactly like the owner-access
  // mutations do. The connection id rides along on the mutation response (parcel.connection_id);
  // without the access-graph refresh the requisition modal keeps showing an already-in-field /
  // already-secured owner as still sendable, and the ParcelsTab status stays stale.
  const inv = (h?: WpwHandover) => {
    qc.invalidateQueries({ queryKey: ["pp", "wpw-handovers"] });
    const connId = h?.parcel?.connection_id;
    if (connId) invalidateAccessGraph(qc, connId);
  };
  return {
    // status set without auto-coupling a file; an acquisition status with `file` sends it.
    setStatus: useMutation({
      mutationFn: (v: { id: string; status: string; note?: string; file?: File | null }) =>
        api.setWpwDeclaredStatus(v.id, v.status, { note: v.note, file: v.file }),
      onSuccess: inv,
    }),
    addComment: useMutation({
      mutationFn: (v: { id: string; body: string }) => api.addWpwComment(v.id, v.body),
      onSuccess: inv,
    }),
    addFiles: useMutation({
      mutationFn: (v: { id: string; files: File[]; docKind?: "other" | "porozumienie" }) =>
        api.addWpwFiles(v.id, v.files, v.docKind ?? "other"),
      onSuccess: inv,
    }),
    // WPW bez zamówienia: zgoda / odmowa wgrana przez koordynatora, bez akceptacji.
    manualOutcome: useMutation({
      mutationFn: (v: { id: string; outcome: "consent" | "refusal"; files: File[]; note?: string }) =>
        api.recordWpwManualOutcome(v.id, v),
      onSuccess: inv,
    }),
    // Decyzja GK o pojedynczym porozumieniu — gasi chip „porozumienie do zatwierdzenia".
    reviewFile: useMutation({
      mutationFn: (v: { id: string; fileId: string; decision: "approved" | "rejected"; note?: string }) =>
        api.reviewWpwFile(v.id, v.fileId, v.decision, v.note ?? ""),
      onSuccess: inv,
    }),
    // Usunięcie pliku negocjatora — w historii zostaje sam wpis (nazwa, kto, kiedy), bez binarki.
    removeFile: useMutation({
      mutationFn: (v: { id: string; fileId: string }) => api.removeWpwFile(v.id, v.fileId),
      onSuccess: inv,
    }),
    // GK: odpięcie nieaktualnego pliku pakietu (gk_docs) — wpis zostaje w osi czasu WPW.
    removeGkDoc: useMutation({
      mutationFn: (v: { id: string; fileId: string }) => api.removeWpwGkDoc(v.id, v.fileId),
      onSuccess: inv,
    }),
    // GK→wykonawca: aktualizacja dokumentów wejściowych (powiadamia wykonawcę, gasi route_review).
    updateGkDocs: useMutation({
      mutationFn: (v: { id: string; files: File[]; note?: string }) =>
        api.updateWpwGkDocs(v.id, v.files, v.note),
      onSuccess: inv,
    }),
    // Wykonawca: „Oznacz jako przejrzane" — gasi badge „Zaktualizowana".
    markGkDocsSeen: useMutation({
      mutationFn: (v: { id: string }) => api.markWpwGkDocsSeen(v.id),
      onSuccess: inv,
    }),
    confirm: useMutation({
      mutationFn: (v: { id: string; targetEvent: string | null; decision: string; note?: string }) =>
        api.confirmWpwEntry(v.id, v.targetEvent, v.decision, v.note ?? ""),
      onSuccess: inv,
    }),
    unconfirm: useMutation({
      mutationFn: (v: { id: string; targetEvent: string | null }) =>
        api.unconfirmWpwEntry(v.id, v.targetEvent),
      onSuccess: inv,
    }),
  };
};

// ── design stage: Projekt techniczny ──────────────────────────────────────────────
export function useTechnicalProject(connection: string | undefined) {
  return useQuery({
    queryKey: qk.technicalProject(connection ?? ""),
    queryFn: () => api.getTechnicalProject(connection!),
    enabled: !!connection,
  });
}

// Subcontractor entry point (from their Order page). Self-hides when the order isn't a design
// order or isn't theirs (the endpoint 404s and the panel renders nothing).
export function useTechnicalProjectByOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "technical-project", "by-order", orderId ?? ""],
    queryFn: () => api.getTechnicalProjectByOrder(orderId!),
    enabled: !!orderId,
    retry: false,
  });
}

export const useTechnicalProjectMutations = (connId: string) => {
  const qc = useQueryClient();
  // Stage + deliverable changes can move the progress rollup / timeline → invalidate broadly.
  // The prefix key covers both by-connection (workspace) and by-order (subcontractor) queries.
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "technical-project"] });
    qc.invalidateQueries({ queryKey: ["pp", "project-events", connId] });
    qc.invalidateQueries({ queryKey: ["pp", "cost-estimate"] }); // pt_accepted / PT-order warnings
    invalidateConnection(qc, connId);
  };
  return {
    assignEmployee: useMutation({
      mutationFn: (v: { id: string; employee: string }) => api.assignTechnicalEmployee(v.id, v.employee),
      onSuccess: inv,
    }),
    sendConcept: useMutation({ mutationFn: (id: string) => api.tpSendConcept(id), onSuccess: inv }),
    acceptConcept: useMutation({ mutationFn: (id: string) => api.tpAcceptConcept(id), onSuccess: inv }),
    requestConceptChange: useMutation({ mutationFn: (id: string) => api.tpRequestConceptChange(id), onSuccess: inv }),
    finalizeRoute: useMutation({
      mutationFn: (v: { id: string; route_version?: string | null }) => api.tpFinalizeRoute(v.id, v.route_version),
      onSuccess: inv,
    }),
    revertRoute: useMutation({
      mutationFn: (v: { id: string; reason?: string }) => api.tpRevertRoute(v.id, v.reason),
      onSuccess: inv,
    }),
    submitBundle: useMutation({
      mutationFn: (v: { id: string; target: "ze" | "urzad" | "tom_prawny" }) => api.tpSubmitBundle(v.id, v.target),
      onSuccess: inv,
    }),
    acceptBundle: useMutation({
      mutationFn: (v: { id: string; target: "ze" | "urzad" | "tom_prawny" }) => api.tpAcceptBundle(v.id, v.target),
      onSuccess: inv,
    }),
    rejectBundle: useMutation({
      mutationFn: (v: { id: string; target: "ze" | "urzad" | "tom_prawny"; note?: string }) =>
        api.tpRejectBundle(v.id, v.target, v.note ?? ""),
      onSuccess: inv,
    }),
    receiveZe: useMutation({ mutationFn: (id: string) => api.tpReceiveZe(id), onSuccess: inv }),
    settle: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.tpSettle(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    addDeliverable: useMutation({
      mutationFn: (v: { id: string; label: string; target?: string }) => api.tpAddDeliverable(v.id, v.label, v.target),
      onSuccess: inv,
    }),
    setState: useMutation({
      mutationFn: (v: { id: string; state: string; note?: string }) => api.setDeliverableState(v.id, v.state, v.note ?? ""),
      onSuccess: inv,
    }),
    accept: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.acceptDeliverable(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    requestCorrection: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.requestDeliverableCorrection(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    addComment: useMutation({
      mutationFn: (v: { id: string; body: string }) => api.addDeliverableComment(v.id, v.body),
      onSuccess: inv,
    }),
    addFiles: useMutation({
      mutationFn: (v: { id: string; files: File[] }) => api.addDeliverableFiles(v.id, v.files),
      onSuccess: inv,
    }),
    replaceFile: useMutation({
      mutationFn: (v: { id: string; attachment: string; file: File }) => api.replaceDeliverableFile(v.id, v.attachment, v.file),
      onSuccess: inv,
    }),
    removeFile: useMutation({
      mutationFn: (v: { id: string; attachment: string }) => api.removeDeliverableFile(v.id, v.attachment),
      onSuccess: inv,
    }),
    deleteDeliverable: useMutation({
      mutationFn: (id: string) => api.deleteDeliverable(id),
      onSuccess: inv,
    }),
    createRequisition: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.createTechnicalRequisition(v.id, v.fd),
      onSuccess: inv,
    }),
    // Postępowanie ofertowe zamiast zlecenia wprost — patrz StageProceedingPanel.
    // („proceeding" w tej featurce oznacza też wątek korespondencji z urzędem — stąd RFQ w nazwie.)
    startRfqProceeding: useMutation({
      mutationFn: (v: { id: string; payload: StageProceedingInput }) =>
        api.createTechnicalProceeding(v.id, v.payload),
      onSuccess: inv,
    }),
    releaseRfqProceeding: useMutation({
      mutationFn: (id: string) => api.releaseTechnicalProceeding(id),
      onSuccess: inv,
    }),
  };
};

// ── design stage: Kosztorys ───────────────────────────────────────────────────────
export function useCostEstimate(connection: string | undefined) {
  return useQuery({
    queryKey: qk.costEstimate(connection ?? ""),
    queryFn: () => api.getCostEstimate(connection!),
    enabled: !!connection,
  });
}

export function useCostEstimateByOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "cost-estimate", "by-order", orderId ?? ""],
    queryFn: () => api.getCostEstimateByOrder(orderId!),
    enabled: !!orderId,
    retry: false,
  });
}

export const useCostEstimateMutations = (connId: string) => {
  const qc = useQueryClient();
  const inv = () => {
    // Prefix key covers BOTH by-connection (workspace) and by-order (subcontractor) queries —
    // the narrower qk.costEstimate(connId) misses the by-order view, leaving it stale until F5.
    qc.invalidateQueries({ queryKey: ["pp", "cost-estimate"] });
    qc.invalidateQueries({ queryKey: ["pp", "project-events", connId] });
    invalidateConnection(qc, connId);
  };
  return {
    assignEmployee: useMutation({
      mutationFn: (v: { id: string; employee: string }) => api.assignCostEmployee(v.id, v.employee),
      onSuccess: inv,
    }),
    setState: useMutation({
      mutationFn: (v: { id: string; state: string; note?: string }) => api.setCostState(v.id, v.state, v.note ?? ""),
      onSuccess: inv,
    }),
    acceptGk: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.acceptCostGk(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    requestCorrection: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.requestCostCorrection(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    settle: useMutation({
      mutationFn: (v: { id: string; note?: string }) => api.settleCost(v.id, v.note ?? ""),
      onSuccess: inv,
    }),
    addComment: useMutation({
      mutationFn: (v: { id: string; body: string }) => api.addCostComment(v.id, v.body),
      onSuccess: inv,
    }),
    addFiles: useMutation({
      mutationFn: (v: { id: string; files: File[] }) => api.addCostFiles(v.id, v.files),
      onSuccess: inv,
    }),
    replaceFile: useMutation({
      mutationFn: (v: { id: string; attachment: string; file: File }) => api.replaceCostFile(v.id, v.attachment, v.file),
      onSuccess: inv,
    }),
    removeFile: useMutation({
      mutationFn: (v: { id: string; attachment: string }) => api.removeCostFile(v.id, v.attachment),
      onSuccess: inv,
    }),
    createRequisition: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.createCostRequisition(v.id, v.fd),
      onSuccess: inv,
    }),
    startRfqProceeding: useMutation({
      mutationFn: (v: { id: string; payload: StageProceedingInput }) =>
        api.createCostProceeding(v.id, v.payload),
      onSuccess: inv,
    }),
    releaseRfqProceeding: useMutation({
      mutationFn: (id: string) => api.releaseCostProceeding(id),
      onSuccess: inv,
    }),
  };
};

// ── connection file attachments ───────────────────────────────────────────────
export function useConnectionFiles(connectionId: string) {
  return useQuery({
    queryKey: qk.connectionFiles(connectionId),
    queryFn: () => api.getConnectionFiles(connectionId),
    enabled: !!connectionId,
  });
}

export function useProjectFolders(connectionId: string) {
  return useQuery({
    queryKey: qk.projectFolders(connectionId),
    queryFn: () => api.getProjectFolders(connectionId),
    enabled: !!connectionId,
  });
}

// DLC 11 — rysunki przedsięwzięcia do otwarcia w przeglądarce.
export function useRysunki(connectionId: string) {
  return useQuery({
    queryKey: qk.rysunki(connectionId),
    queryFn: () => pobierzRysunki(connectionId),
    enabled: !!connectionId,
  });
}

export function useSetFolderConfidentiality(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Endpoint zwraca świeże drzewo — wpisujemy je prosto do cache zamiast invalidacji.
    mutationFn: (v: { folderSlug?: string; managedFileId?: string; isConfidential: boolean }) =>
      api.setFolderConfidentiality(
        connectionId,
        { folderSlug: v.folderSlug, managedFileId: v.managedFileId },
        v.isConfidential,
      ),
    onSuccess: (tree) => qc.setQueryData(qk.projectFolders(connectionId), tree),
  });
}

export function useCreateProjectFolder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => api.createProjectFolder(connectionId, label),
    onSuccess: (tree) => qc.setQueryData(qk.projectFolders(connectionId), tree),
  });
}

export function useRenameProjectFolder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { slug: string; label: string }) =>
      api.renameProjectFolder(connectionId, v.slug, v.label),
    onSuccess: (tree) => qc.setQueryData(qk.projectFolders(connectionId), tree),
  });
}

// Usunięcie folderu własnego — pliki wracają do kosza „Inne / bez folderu".
export function useDeleteProjectFolder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.deleteProjectFolder(connectionId, slug),
    onSuccess: (tree) => qc.setQueryData(qk.projectFolders(connectionId), tree),
  });
}

// Odpięcie wypisu od działek z folderu WYPISY (zakładka Pliki). Backend oddaje świeże
// drzewo; odświeżamy też listę wypisów i działek zakładki Działki, bo znika tam powiązanie.
export function useDetachWypisFromFolder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (wypisId: string) => api.detachWypisFromFolder(connectionId, wypisId),
    onSuccess: (tree) => {
      qc.setQueryData(qk.projectFolders(connectionId), tree);
      qc.invalidateQueries({ queryKey: qk.wypisy(connectionId) });
      qc.invalidateQueries({ queryKey: qk.parcels(connectionId) });
    },
  });
}

export function useTransferProjectFiles(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      operation: "copy" | "move";
      target_slug: string;
      managed_file_id?: string;
      connection_file_id?: string;
      source_slug?: string;
    }) => api.transferProjectFiles(connectionId, v),
    onSuccess: (tree) => qc.setQueryData(qk.projectFolders(connectionId), tree),
  });
}

// Zamówienia przedsięwzięcia do wyboru w oknie „udostępnij w zleceniu".
export function useShareableOrders(connectionId: string, enabled = true) {
  return useQuery({
    queryKey: qk.shareableOrders(connectionId),
    queryFn: () => api.getShareableOrders(connectionId),
    enabled: !!connectionId && enabled,
  });
}

// Udostępnienie w zamówieniu: `scope: "all"` = wszystkie niepoufne (reguła żywa), inaczej
// punktowo folder albo pojedynczy plik. Odpowiedź to świeże drzewo ze znacznikami.
export function useShareToOrder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      order: string; scope?: "all"; folderSlug?: string; managedFileId?: string;
    }) =>
      api.shareToOrder(connectionId, {
        order: v.order, scope: v.scope,
        folder_slug: v.folderSlug, managed_file_id: v.managedFileId,
      }),
    onSuccess: (tree, v) => {
      // Znaczniki „udostępnione w…" przy folderach/plikach + panel udostępnień.
      qc.setQueryData(qk.projectFolders(connectionId), tree);
      // Sekcja „Pliki z projektu ZE" w zamówieniu + flaga has_project_files_share.
      qc.invalidateQueries({ queryKey: ["orders", v.order] });
      qc.invalidateQueries({ queryKey: qk.shareableOrders(connectionId) });
    },
  });
}

// Cofnięcie udostępnienia — całości, folderu albo pojedynczego pliku.
export function useUnshareFromOrder(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      order: string; scope?: "all"; folderSlug?: string; managedFileId?: string;
    }) =>
      api.unshareFromOrder(connectionId, {
        order: v.order, scope: v.scope,
        folder_slug: v.folderSlug, managed_file_id: v.managedFileId,
      }),
    onSuccess: (tree, v) => {
      qc.setQueryData(qk.projectFolders(connectionId), tree);
      qc.invalidateQueries({ queryKey: ["orders", v.order] });
      qc.invalidateQueries({ queryKey: qk.shareableOrders(connectionId) });
    },
  });
}

export function useConnectionFileMutations(connectionId: string) {
  const qc = useQueryClient();
  // Manual-folder uploads/deletes change both the flat list and the folder-tree counts.
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.connectionFiles(connectionId) });
    qc.invalidateQueries({ queryKey: qk.projectFolders(connectionId) });
    // Wypis wgrany do kamienia zakłada wpis na zakładce Działki (lustro w signals) —
    // odświeżamy też listę wypisów, żeby pojawił się bez przeładowania.
    qc.invalidateQueries({ queryKey: qk.wypisy(connectionId) });
  };
  return {
    upload: useMutation({
      mutationFn: (v: {
        file: File; description: string; milestone?: string; folderSlug?: string; milestoneSlot?: string;
      }) =>
        api.uploadConnectionFile(connectionId, v.file, v.description, {
          milestoneId: v.milestone, folderSlug: v.folderSlug, milestoneSlot: v.milestoneSlot,
        }),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; description: string }) =>
        api.updateConnectionFileDescription(v.id, v.description),
      onSuccess: inv,
    }),
    rename: useMutation({
      mutationFn: (v: { id: string; filename: string }) =>
        api.renameConnectionFile(v.id, v.filename),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteConnectionFile(id),
      onSuccess: inv,
    }),
  };
}

// ── wypisy (land registry excerpts) ──────────────────────────────────────────
export function useWypisy(connectionId: string, enabled = true) {
  return useQuery({
    queryKey: qk.wypisy(connectionId),
    queryFn: () => api.getWypisy(connectionId),
    enabled: enabled && !!connectionId,
  });
}

export function useWypisMutations(connectionId: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.wypisy(connectionId) });
  return {
    upload: useMutation({
      mutationFn: (v: { file: File; parcelIds: string[]; note: string }) =>
        api.uploadWypisFile(connectionId, v.file, v.parcelIds, v.note),
      onSuccess: inv,
    }),
    setPageMap: useMutation({
      mutationFn: (v: { id: string; pageMap: WypisPageEntry[]; note?: string }) =>
        api.setWypisPageMap(v.id, v.pageMap, v.note),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteWypisFile(id),
      onSuccess: inv,
    }),
    // Odpięcie od jednej działki — może skasować właścicieli, więc odświeża też działki.
    detachParcel: useMutation({
      mutationFn: (v: { id: string; parcelId: string; removeOwners: boolean }) =>
        api.detachWypisParcel(v.id, v.parcelId, v.removeOwners),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcels(connectionId) });
      },
    }),
    // AI extraction of a single manual wypis (no order). extract only starts the task —
    // the page polls it; apply writes owners/fields, so it also refreshes the parcels.
    extract: useMutation({ mutationFn: (id: string) => api.extractWypisFile(id) }),
    apply: useMutation({
      mutationFn: (v: { id: string; parcels: WypisProposalParcel[] }) =>
        api.applyWypisFileExtraction(v.id, v.parcels),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: qk.parcels(connectionId) });
      },
    }),
  };
}

// ── wnioski urzędowe (silnik pakietów) ─────────────────────────────────────────
export const useApplicationRegistry = () =>
  useQuery({ queryKey: qk.appRegistry, queryFn: api.getApplicationRegistry, staleTime: Infinity });

export const useApplicationsOverview = (connectionId: string | undefined) =>
  useQuery({
    queryKey: qk.appOverview(connectionId ?? ""),
    queryFn: () => api.getApplicationsOverview(connectionId!),
    enabled: !!connectionId,
  });

export const useApplications = (filters: Record<string, string> = {}) =>
  useQuery({
    queryKey: qk.applications(filters),
    queryFn: () => api.getApplications(filters),
    refetchInterval: 60_000, // opłaty/statusy wracają bez przeładowania
  });

// Kolejka „Wnioski do akceptacji" — klucz pod ["pp", "applications"], więc każda
// mutacja wniosku (useApplicationMutations → inv) odświeża ją razem z listami.
export const useApplicationApprovalQueue = () =>
  useQuery({
    queryKey: ["pp", "applications", "approval-queue"] as const,
    queryFn: api.getApplicationApprovalQueue,
    // Obiekt OBIEGU: koordynatorzy dokładają / wycofują wnioski w tle.
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 60_000,
  });

export const useApplication = (id: string | undefined) =>
  useQuery({
    queryKey: qk.application(id ?? ""),
    queryFn: () => api.getApplication(id!),
    enabled: !!id,
    // Wniosek to obiekt OBIEGU (ktoś inny mógł go właśnie przekazać/zwrócić/złożyć):
    // globalny staleTime 60 s + brak refetchu przy fokusie sprawiały, że klik
    // w dzwonek „czeka na akceptację" pokazywał dyrektorowi stary widok szkicu
    // z cache — bez panelu akceptacji (prod, 2026-09-29). Zawsze dociągaj świeży
    // stan przy wejściu; cache i tak renderuje się od razu, bez migotania.
    staleTime: 0,
    refetchOnMount: "always",
  });

// Propozycja sklejenia stron wypisów (modal w checkliście załączników).
export const useWypisMergeProposal = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wypis-merge-proposal", id],
    queryFn: () => api.getWypisMergeProposal(id!),
    enabled: !!id && enabled,
  });

export function useApplicationMutations(applicationId?: string) {
  const qc = useQueryClient();
  const inv = () => {
    if (applicationId) qc.invalidateQueries({ queryKey: qk.application(applicationId) });
    qc.invalidateQueries({ queryKey: ["pp", "applications"] });
    qc.invalidateQueries({ queryKey: ["pp", "app-overview"] });
    qc.invalidateQueries({ queryKey: ["pp", "app-poa-candidates"] });
    // Wniosek o klauzulę zmienia rejestr decyzji (wysyłka → „wniosek w toku”,
    // zakończenie → stempel), więc lista decyzji też ma się odświeżyć.
    qc.invalidateQueries({ queryKey: ["pp", "decisions"] });
  };
  return {
    create: useMutation({ mutationFn: api.createApplication, onSuccess: inv }),
    // Komplet wniosków lokalizacyjnych naraz — po jednym na właściciela instytucjonalnego.
    bulkCreate: useMutation({ mutationFn: api.bulkCreateApplications, onSuccess: inv }),
    // Formatki dla całego kompletu — po jednym zestawie na wniosek.
    bulkFormatki: useMutation({ mutationFn: api.bulkGenerateFormatki, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Record<string, unknown> }) =>
        api.updateApplication(v.id, v.payload),
      onSuccess: inv,
    }),
    approvePhase: useMutation({
      mutationFn: (v: { id: string; phase: string; allowUnpaidFees?: boolean }) =>
        api.approveApplicationPhase(v.id, v.phase, v.allowUnpaidFees),
      onSuccess: inv,
    }),
    reopenPhase: useMutation({
      mutationFn: (v: { id: string; phase: string }) => api.reopenApplicationPhase(v.id, v.phase),
      onSuccess: inv,
    }),
    resolvePoa: useMutation({ mutationFn: (id: string) => api.resolveApplicationPoa(id), onSuccess: inv }),
    attachPoa: useMutation({
      mutationFn: (v: { id: string; poaId: string }) => api.attachApplicationPoa(v.id, v.poaId),
      onSuccess: inv,
    }),
    detachPoa: useMutation({
      mutationFn: (v: { id: string; linkId: string }) => api.detachApplicationPoa(v.id, v.linkId),
      onSuccess: inv,
    }),
    generateScopeMap: useMutation({
      mutationFn: (id: string) => api.generateApplicationScopeMap(id),
      onSuccess: inv,
    }),
    generateEnergaDescription: useMutation({
      mutationFn: (id: string) => api.generateApplicationEnergaDescription(id),
      onSuccess: inv,
    }),
    generateWpwForms: useMutation({
      mutationFn: (v: { id: string; kinds?: string[] }) => api.generateApplicationWpwForms(v.id, v.kinds),
      // inv po ZAKOŃCZENIU taska robi strona (polling) — tu tylko start w tle
    }),
    setWpwFormDescriptions: useMutation({
      mutationFn: (v: { id: string; descriptions: Record<string, string> }) =>
        api.setApplicationWpwFormDescriptions(v.id, v.descriptions),
      onSuccess: inv,
    }),
    generateWpwDescriptions: useMutation({
      mutationFn: (id: string) => api.generateApplicationWpwDescriptions(id),
      onSuccess: inv,
    }),
    generateUlicpParams: useMutation({
      mutationFn: (id: string) => api.generateApplicationUlicpParams(id),
      onSuccess: inv,
    }),
    computeRoadData: useMutation({
      mutationFn: (id: string) => api.computeApplicationRoadData(id),
      onSuccess: inv,
    }),
    suggestRelacja: useMutation({
      mutationFn: (id: string) => api.suggestApplicationRelacja(id),
      onSuccess: inv,
    }),
    generateFormatki: useMutation({
      mutationFn: (v: { id: string; opts?: Parameters<typeof api.generateApplicationFormatki>[1] }) =>
        api.generateApplicationFormatki(v.id, v.opts),
      // inv po ZAKOŃCZENIU taska robi strona (polling) — tu tylko start w tle
    }),
    setPoaFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.setApplicationPoaFee>[1] }) =>
        api.setApplicationPoaFee(v.id, v.payload),
      onSuccess: inv,
    }),
    // Ręczna korekta urzędu opłaty skarbowej ("" = powrót do rozpoznania z siedziby
    // mocodawcy) — dotyczy całego wniosku, nie pojedynczego ogniwa łańcucha.
    setStampDutyOffice: useMutation({
      mutationFn: (v: { id: string; office: string }) =>
        api.setApplicationStampDutyOffice(v.id, v.office),
      onSuccess: inv,
    }),
    // Opłata skarbowa OD WNIOSKU (ULICP 598 zł) + ręczna korekta jej urzędu.
    setApplicationFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.setApplicationFee>[1] }) =>
        api.setApplicationFee(v.id, v.payload),
      onSuccess: inv,
    }),
    setApplicationFeeOffice: useMutation({
      mutationFn: (v: { id: string; office: string }) =>
        api.setApplicationFeeOffice(v.id, v.office),
      onSuccess: inv,
    }),
    setQualification: useMutation({
      mutationFn: (v: {
        id: string;
        overrides: {
          parcel_id: string;
          override?: boolean | null;
          exclusion_area_override_m2?: number | null;
          exclusion_area_note?: string;
          exclusion_land_class?: string;
        }[];
      }) => api.setApplicationQualification(v.id, v.overrides),
      onSuccess: inv,
    }),
    ownDocument: useMutation({
      mutationFn: (v: { id: string; file: File | null }) => api.uploadApplicationOwnDocument(v.id, v.file),
      onSuccess: inv,
    }),
    attachMap: useMutation({
      mutationFn: (v: { id: string; sheetSetId: string }) => api.attachApplicationMap(v.id, v.sheetSetId),
      onSuccess: inv,
    }),
    uploadMap: useMutation({
      mutationFn: (v: { id: string; file: File; label?: string }) =>
        api.uploadApplicationMap(v.id, v.file, v.label),
      onSuccess: inv,
    }),
    deleteMap: useMutation({
      mutationFn: (v: { id: string; mapId: string }) => api.deleteApplicationMap(v.id, v.mapId),
      onSuccess: inv,
    }),
    uploadAttachment: useMutation({
      mutationFn: (v: { id: string; kind: string; files: File[]; note?: string }) =>
        api.uploadApplicationAttachment(v.id, v.kind, v.files, v.note),
      onSuccess: inv,
    }),
    mergeWypis: useMutation({
      mutationFn: (v: { id: string; selections: { wypis: string; pages: number[] }[] }) =>
        api.mergeWypisPages(v.id, v.selections),
      onSuccess: inv,
    }),
    deleteAttachment: useMutation({
      mutationFn: (v: { id: string; attId: string }) => api.deleteApplicationAttachment(v.id, v.attId),
      onSuccess: inv,
    }),
    attachFeeConfirmation: useMutation({
      mutationFn: (v: { id: string; kind: string; feeId: string }) =>
        api.attachApplicationFeeConfirmation(v.id, v.kind, v.feeId),
      onSuccess: inv,
    }),
    compile: useMutation({
      mutationFn: (v: { id: string; allowUnpaidFees?: boolean }) =>
        api.compileApplication(v.id, v.allowUnpaidFees),
    }),
    uploadCompiled: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadApplicationCompiled(v.id, v.file),
      onSuccess: inv,
    }),
    submit: useMutation({
      mutationFn: (v: { id: string; channel: string; channelRef?: string; allowUnpaidFees?: boolean }) =>
        api.submitOfficialApplication(v.id, v.channel, v.channelRef, v.allowUnpaidFees),
      onSuccess: inv,
    }),
    // Obieg akceptacji wysyłki: przekazanie do dyrektora / wycofanie / zwrot do poprawy.
    requestApproval: useMutation({
      mutationFn: (v: { id: string; channel: string; channelRef?: string }) =>
        api.requestApplicationApproval(v.id, v.channel, v.channelRef),
      onSuccess: inv,
    }),
    withdrawApproval: useMutation({
      mutationFn: (id: string) => api.withdrawApplicationApproval(id),
      onSuccess: inv,
    }),
    // Wycofanie złożonego wniosku kasuje sprawę automatyczną i zmienia DLC_02 / DLC_09 —
    // odśwież też Sprawy / działki projektu.
    withdrawSubmission: useMutation({
      mutationFn: (v: { id: string; confirmUntracked?: boolean }) =>
        api.withdrawApplicationSubmission(v.id, v.confirmUntracked),
      onSuccess: (app) => { inv(); invalidateAccessGraph(qc, app.connection); },
    }),
    rejectApproval: useMutation({
      mutationFn: (v: { id: string; note: string }) =>
        api.rejectApplicationApproval(v.id, v.note),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelApplication(id), onSuccess: inv }),
    complete: useMutation({
      mutationFn: (v: { id: string } & api.CompleteApplicationPayload) =>
        api.completeApplication(v.id, v),
      // Zakończenie dokłada odpowiedź do sprawy i ją zamyka → odśwież też Sprawy / działki.
      onSuccess: (app) => { inv(); invalidateAccessGraph(qc, app.connection); },
    }),
    // Klauzula: ręczna korekta dat obiegu ORYGINAŁU decyzji (wyszedł / wrócił).
    decisionOriginal: useMutation({
      mutationFn: (v: { id: string; sent_at?: string | null; returned_at?: string | null }) =>
        api.setApplicationDecisionOriginal(v.id, { sent_at: v.sent_at, returned_at: v.returned_at }),
      onSuccess: inv,
    }),
    // Podpięcie / odpięcie sprawy z Korespondencji (zamkniętą otwiera `reopen_case`).
    linkProceeding: useMutation({
      mutationFn: (v: {
        id: string; proceeding: string | null; proceeding_item?: string | null; reopen_case?: boolean;
        delete_case?: boolean;
      }) =>
        api.linkApplicationProceeding(v.id, {
          proceeding: v.proceeding, proceeding_item: v.proceeding_item,
          ...(v.reopen_case ? { reopen_case: true } : {}),
          ...(v.delete_case ? { delete_case: true } : {}),
        }),
      onSuccess: (app) => {
        inv();
        invalidateAccessGraph(qc, app.connection);
      },
    }),
  };
}

export const useApplicationAutoMaps = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.appAutoMaps(id ?? ""),
    queryFn: () => api.getApplicationAutoMaps(id!),
    enabled: enabled && !!id,
  });

export const usePoaFeeCandidates = (enabled = true) =>
  useQuery({ queryKey: qk.poaFeeCandidates, queryFn: api.getPoaFeeCandidates, enabled });

// Opłaty do wskazania jako opłata skarbowa OD WNIOSKU (ULICP 598 zł) — nie mieszają
// się z opłatami za pełnomocnictwo: tamte mają is_power_of_attorney, te nie.
export const useApplicationFeeCandidates = (appId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "app-fee-candidates", appId] as const,
    queryFn: () => api.getApplicationFeeCandidates(appId),
    enabled: enabled && !!appId,
  });

// Opłacone opłaty do podpięcia jako załącznik „potwierdzenie opłaty skarbowej".
export const useApplicationPaidFeeCandidates = (appId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "app-paid-fee-candidates", appId] as const,
    queryFn: () => api.getApplicationPaidFeeCandidates(appId),
    enabled: enabled && !!appId,
  });

// Własne opłaty użytkownika do podpięcia pod DOO (modale opłaty + edycja DOO).
// Ten sam zbiór dla wszystkich trzech modułów, ale endpoint per moduł (uprawnienia).
const DOO_FEE_CANDIDATES_API = {
  wypis: api.getWypisFeeCandidates,
  mz: api.getMzFeeCandidates,
  mpzp: api.getMpzpFeeCandidates,
} as const;

export const useDooFeeCandidates = (module: keyof typeof DOO_FEE_CANDIDATES_API, enabled = true) =>
  useQuery({
    queryKey: ["pp", "doo-fee-candidates", module] as const,
    queryFn: DOO_FEE_CANDIDATES_API[module],
    enabled,
  });

export const useApplicationPoaCandidates = (appId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "app-poa-candidates", appId],
    queryFn: () => api.getApplicationPoaCandidates(appId),
    enabled: enabled && !!appId,
  });

export const usePoaFeeSuggestion = (appId: string, enabled = true) =>
  useQuery({
    queryKey: qk.poaFeeSuggestion(appId),
    queryFn: () => api.getPoaFeeSuggestion(appId),
    enabled: enabled && !!appId,
    staleTime: 30_000,
  });

export const useWypisPoaFeeSuggestion = (orderId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wypis-poa-fee-suggestion", orderId],
    queryFn: () => api.getWypisPoaFeeSuggestion(orderId),
    enabled: enabled && !!orderId,
    staleTime: 30_000,
  });

// ── rejestr pełnomocnictw + decyzji ─────────────────────────────────────────────
export const usePowersOfAttorney = (filters: Record<string, string> = {}) =>
  useQuery({ queryKey: qk.poas(filters), queryFn: () => api.getPowersOfAttorney(filters) });

export const useExpiringPoas = () =>
  useQuery({ queryKey: qk.poasExpiring, queryFn: api.getExpiringPoas });

export function usePoaMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "poas"] });
    qc.invalidateQueries({ queryKey: qk.poasExpiring });
  };
  return {
    create: useMutation({ mutationFn: api.createPowerOfAttorney, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<import("./types").PowerOfAttorneyRow> }) =>
        api.updatePowerOfAttorney(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deletePowerOfAttorney(id), onSuccess: inv }),
    uploadFile: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadPoaFile(v.id, v.file),
      onSuccess: inv,
    }),
    setClientConfig: useMutation({
      mutationFn: api.setPoaClientConfig,
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["pp", "poa-client-config"] });
        qc.invalidateQueries({ queryKey: qk.poaConfigs });
      },
    }),
    setOfficeConfig: useMutation({
      mutationFn: api.setPoaOfficeConfig,
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["pp", "poa-office-config"] });
        qc.invalidateQueries({ queryKey: qk.poaConfigs });
      },
    }),
  };
}

export const usePoaClientConfig = (clientId?: string, elektim?: boolean) =>
  useQuery({
    queryKey: qk.poaClientConfig(elektim ? "elektim" : clientId ?? ""),
    queryFn: () => api.getPoaClientConfig(clientId, elektim),
    enabled: !!clientId || !!elektim,
  });

export const usePoaOfficeConfig = (officeId?: string) =>
  useQuery({
    queryKey: qk.poaOfficeConfig(officeId ?? ""),
    queryFn: () => api.getPoaOfficeConfig(officeId!),
    enabled: !!officeId,
  });

export const usePoaConfigs = () =>
  useQuery({ queryKey: qk.poaConfigs, queryFn: api.getPoaConfigs });

// Optional PoA scoping pickers: przedsięwzięcia (all, or narrowed to a client) and a
// client's oddziały (only fetched once a client is chosen).
export const usePoaProjects = (clientId?: string) =>
  useQuery({
    queryKey: qk.poaProjects(clientId ?? ""),
    queryFn: () => api.getPoaProjects(clientId),
  });

export const useClientBranches = (clientId?: string) =>
  useQuery({
    queryKey: qk.clientBranches(clientId ?? ""),
    queryFn: () => api.getClientBranches(clientId),
    enabled: !!clientId,
  });

export const useDecisions = (filters: Record<string, string> = {}) =>
  useQuery({ queryKey: qk.decisions(filters), queryFn: () => api.getDecisions(filters) });

export function useDecisionMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "decisions"] });
    qc.invalidateQueries({ queryKey: ["pp", "app-overview"] });
    qc.invalidateQueries({ queryKey: ["pp", "application"] });
  };
  return {
    create: useMutation({ mutationFn: api.createDecision, onSuccess: inv }),
    // Decyzja → sprawa jako pozycja „odpowiedź" (może zamknąć sprawę) → odśwież Sprawy.
    attachToProceeding: useMutation({
      mutationFn: (v: { id: string; proceeding: string; closes_thread?: boolean; outcome?: string; note?: string }) =>
        api.attachDecisionToProceeding(v.id, v),
      onSuccess: (d) => { inv(); invalidateAccessGraph(qc, d.connection); },
    }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Record<string, unknown> }) =>
        api.updateDecision(v.id, v.payload),
      onSuccess: inv,
    }),
    uploadFile: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadDecisionFile(v.id, v.file),
      onSuccess: inv,
    }),
    markClause: useMutation({
      mutationFn: (v: { id: string; finalAt: string; file?: File }) =>
        api.markDecisionClause(v.id, v.finalAt, v.file),
      onSuccess: inv,
    }),
    clearClause: useMutation({
      mutationFn: (v: { id: string; reason: string }) => api.clearDecisionClause(v.id, v.reason),
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["pp", "clause-impact"] });
      },
    }),
  };
}

/** Konsekwencje cofnięcia klauzuli — pobierane dopiero po otwarciu modala. */
export const useClauseRevertImpact = (decisionId: string | null) =>
  useQuery({
    queryKey: ["pp", "clause-impact", decisionId] as const,
    queryFn: () => api.getClauseRevertImpact(decisionId as string),
    enabled: !!decisionId,
  });

// ── wypis orders (zamawianie wypisów EGiB per powiat) ──────────────────────────
// Modal „Zamów ponownie wypisy” (panel zamawiania): działki projektu, które mają już wypis.
export const useWypisReorderOptions = (connectionId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "wypis-reorder-options", "connection", connectionId ?? ""] as const,
    queryFn: () => api.getWypisReorderOptions({ connection: connectionId! }),
    enabled: !!connectionId,
  });

export function useWypisCandidates(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.wypisCandidates(filters),
    queryFn: () => api.getWypisCandidates(filters),
  });
}

// 60s refetch so an order paid by the small-account user (or a fee decision) surfaces
// in the panel without a reload — same idiom the official-fees badges use.
export function useWypisOrders(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.wypisOrders(filters),
    queryFn: () => api.getWypisOrders(filters),
    refetchInterval: 60_000,
  });
}

export function useWypisOrder(id: string | undefined) {
  return useQuery({
    queryKey: qk.wypisOrder(id ?? ""),
    queryFn: () => api.getWypisOrder(id!),
    enabled: !!id,
  });
}

export function useWypisOrderMutations(orderId?: string) {
  const qc = useQueryClient();
  const inv = () => {
    if (orderId) qc.invalidateQueries({ queryKey: qk.wypisOrder(orderId) });
    qc.invalidateQueries({ queryKey: ["pp", "wypis-orders"] });
    qc.invalidateQueries({ queryKey: ["pp", "wypis-candidates"] });
    // Tabela działek pokazuje przy działce ślad jej zamówienia wypisu (parcel.wypis_orders),
    // więc każda zmiana zamówienia musi odświeżyć listy działek otwartych projektów.
    qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
  };
  return {
    create: useMutation({ mutationFn: api.createWypisOrder, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.updateWypisOrder>[1] }) =>
        api.updateWypisOrder(v.id, v.payload),
      onSuccess: inv,
    }),
    generate: useMutation({ mutationFn: (id: string) => api.generateWypisDocuments(id) }),
    requestFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.requestWypisFee>[1] }) =>
        api.requestWypisFee(v.id, v.payload),
      onSuccess: inv,
    }),
    editDoo: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.editWypisDoo>[1] }) =>
        api.editWypisDoo(v.id, v.payload),
      onSuccess: inv,
    }),
    markSent: useMutation({
      mutationFn: (v: { id: string; sentNote?: string }) => api.markWypisSent(v.id, v.sentNote),
      onSuccess: inv,
    }),
    setPoaFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.setWypisPoaFee>[1] }) =>
        api.setWypisPoaFee(v.id, v.payload),
      onSuccess: inv,
    }),
    markReceived: useMutation({ mutationFn: (id: string) => api.markWypisReceived(id), onSuccess: inv }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelWypisOrder(id), onSuccess: inv }),
    changeOrderer: useMutation({
      mutationFn: (v: { id: string; userId: string }) => api.changeWypisOrderer(v.id, v.userId),
      onSuccess: inv,
    }),
    revert: useMutation({
      mutationFn: (v: { id: string; reason?: string }) => api.revertWypisOrder(v.id, v.reason),
      onSuccess: inv,
    }),
    uploadResultFiles: useMutation({
      mutationFn: (v: { id: string; files: File[]; other?: boolean }) =>
        api.uploadWypisResultFiles(v.id, v.files, v.other),
      onSuccess: inv,
    }),
    deleteResultFile: useMutation({
      mutationFn: (v: { id: string; resultFileId: string }) =>
        api.deleteWypisResultFile(v.id, v.resultFileId),
      onSuccess: inv,
    }),
    extract: useMutation({ mutationFn: (id: string) => api.extractWypisResults(id) }),
    // After apply, the new owners/wypisy must show up in every touched project's
    // Działki tab — invalidate their parcels + wypisy queries by connection id.
    apply: useMutation({
      mutationFn: (v: { id: string; parcels: WypisProposalParcel[] }) =>
        api.applyWypisExtraction(v.id, v.parcels),
      onSuccess: (order) => {
        inv();
        const connections = new Set(order.parcels_brief.map((p) => p.connection_id));
        connections.forEach((c) => {
          qc.invalidateQueries({ queryKey: qk.parcels(c) });
          qc.invalidateQueries({ queryKey: qk.wypisy(c) });
        });
      },
    }),
  };
}

// ── mz orders (zamawianie mapy zasadniczej na obrys MDCP) ──────────────────────
// 60s refetch — jak przy wypisach: opłacona opłata / decyzja pojawia się bez reloadu.
export function useMdcpOrders(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.mdcpOrders(filters),
    queryFn: () => api.getMdcpOrders(filters),
    refetchInterval: 60_000,
  });
}

export function useMzOrders(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.mzOrders(filters),
    queryFn: () => api.getMzOrders(filters),
    refetchInterval: 60_000,
  });
}

export function useMzOrder(id: string | undefined) {
  return useQuery({
    queryKey: qk.mzOrder(id ?? ""),
    queryFn: () => api.getMzOrder(id!),
    enabled: !!id,
  });
}

/** Obrysy (bufory) MDCP projektu — lista rozwijana zakresu: panel MDCP i wniosek MZ. */
export function useMdcpBuffers(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "mdcp-buffers", connectionId],
    queryFn: () => api.getMdcpBuffers(connectionId!),
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useMzSuggestContext(connectionId: string | undefined) {
  return useQuery({
    queryKey: qk.mzSuggestContext(connectionId ?? ""),
    queryFn: () => api.getMzSuggestContext(connectionId!),
    enabled: !!connectionId,
  });
}

export const useMzPoaFeeSuggestion = (orderId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "mz-poa-fee-suggestion", orderId] as const,
    queryFn: () => api.getMzPoaFeeSuggestion(orderId),
    enabled,
  });

export function useMzOrderMutations(orderId?: string) {
  const qc = useQueryClient();
  const inv = () => {
    if (orderId) qc.invalidateQueries({ queryKey: qk.mzOrder(orderId) });
    qc.invalidateQueries({ queryKey: ["pp", "mz-orders"] });
  };
  return {
    create: useMutation({ mutationFn: api.createMzOrder, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.updateMzOrder>[1] }) =>
        api.updateMzOrder(v.id, v.payload),
      onSuccess: inv,
    }),
    generate: useMutation({ mutationFn: (id: string) => api.generateMzDocuments(id) }),
    requestFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.requestMzFee>[1] }) =>
        api.requestMzFee(v.id, v.payload),
      onSuccess: inv,
    }),
    editDoo: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.editMzDoo>[1] }) =>
        api.editMzDoo(v.id, v.payload),
      onSuccess: inv,
    }),
    setPoaFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.setMzPoaFee>[1] }) =>
        api.setMzPoaFee(v.id, v.payload),
      onSuccess: inv,
    }),
    addComment: useMutation({
      mutationFn: (v: { id: string; body: string }) => api.addMzOrderComment(v.id, v.body),
      onSuccess: inv,
    }),
    updateComment: useMutation({
      mutationFn: (v: { id: string; commentId: string; body: string }) =>
        api.updateMzOrderComment(v.id, v.commentId, v.body),
      onSuccess: inv,
    }),
    deleteComment: useMutation({
      mutationFn: (v: { id: string; commentId: string }) =>
        api.deleteMzOrderComment(v.id, v.commentId),
      onSuccess: inv,
    }),
    markSent: useMutation({
      mutationFn: (v: { id: string; sentNote?: string }) => api.markMzSent(v.id, v.sentNote),
      onSuccess: inv,
    }),
    markReceived: useMutation({ mutationFn: (id: string) => api.markMzReceived(id), onSuccess: inv }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelMzOrder(id), onSuccess: inv }),
    changeOrderer: useMutation({
      mutationFn: (v: { id: string; userId: string }) => api.changeMzOrderer(v.id, v.userId),
      onSuccess: inv,
    }),
    revert: useMutation({
      mutationFn: (v: { id: string; reason?: string }) => api.revertMzOrder(v.id, v.reason),
      onSuccess: inv,
    }),
    complete: useMutation({
      mutationFn: (v: string | { id: string; confirmWithoutFiles?: boolean }) =>
        typeof v === "string"
          ? api.completeMzOrder(v)
          : api.completeMzOrder(v.id, v.confirmWithoutFiles),
      onSuccess: inv,
    }),
    uploadResultFiles: useMutation({
      mutationFn: (v: { id: string; files: File[]; kind?: "map" | "licencja" | "other" }) =>
        api.uploadMzResultFiles(v.id, v.files, v.kind),
      onSuccess: inv,
    }),
    deleteResultFile: useMutation({
      mutationFn: (v: { id: string; resultFileId: string }) =>
        api.deleteMzResultFile(v.id, v.resultFileId),
      onSuccess: inv,
    }),
  };
}

// ── Generacja dokumentów — szablony ODF ──────────────────────────────────────
export const useDocTemplates = () =>
  useQuery({ queryKey: qk.docTemplates, queryFn: api.getDocTemplates });

// Static registry — effectively immutable during a session, so cache it forever.
export const useDocVariables = (enabled = true) =>
  useQuery({ queryKey: qk.docVariables, queryFn: api.getDocVariables, staleTime: Infinity, enabled });

// Historia wgrań pliku jednego szablonu — ładowana dopiero po otwarciu okna historii.
export const useDocTemplateVersions = (templateId: string | undefined) =>
  useQuery({
    queryKey: qk.docTemplateVersions(templateId ?? ""),
    queryFn: () => api.getDocTemplateVersions(templateId!),
    enabled: !!templateId,
  });

// Availability check of a template in a generation context (empty values pass;
// variables the context cannot supply at all block generation).
export const useDocTemplateCheck = (templateId: string, contextKind: string, contextId: string) =>
  useQuery({
    queryKey: qk.docTemplateCheck(templateId, contextKind, contextId),
    queryFn: () => api.checkDocTemplate(templateId, contextKind, contextId),
    enabled: !!templateId && !!contextId,
  });

// Operator (OSD) projektu kontekstu — do doboru „niewspólnych" szablonów per operator.
export const useContextOperator = (contextKind: string, contextId: string) =>
  useQuery({
    queryKey: qk.contextOperator(contextKind, contextId),
    queryFn: () => api.getContextOperator(contextKind, contextId),
    enabled: !!contextKind && !!contextId,
    staleTime: Infinity,
  });

// ── księgi wieczyste (KW) — analiza AI per działka ────────────────────────────
export const useParcelKwFiles = (parcelId: string | undefined) =>
  useQuery({
    queryKey: ["pp", "parcel-kw", parcelId],
    queryFn: () => api.getParcelKwFiles(parcelId!),
    enabled: !!parcelId,
  });

export function useKwMutations(parcelId: string | undefined) {
  const qc = useQueryClient();
  // Werdykt „ANALIZA KW" siedzi też w kolumnie Status na liście działek — każda zmiana
  // księgi (analiza, akceptacja rozbieżności, usunięcie) musi przeliczyć listę,
  // inaczej badge kłamie.
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "parcel-kw", parcelId] });
    qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
    if (parcelId) qc.invalidateQueries({ queryKey: ["pp", "parcel", parcelId] });
  };
  return {
    acceptDiff: useMutation({
      mutationFn: (v: { id: string; kwName?: string; dbName?: string; note?: string }) =>
        api.acceptKwOwnerDiff(v.id, v),
      onSuccess: inv,
    }),
    unacceptDiff: useMutation({
      mutationFn: (v: { id: string; kwName?: string; dbName?: string }) =>
        api.unacceptKwOwnerDiff(v.id, v),
      onSuccess: inv,
    }),
    upload: useMutation({ mutationFn: (file: File) => api.uploadParcelKw(parcelId!, file), onSuccess: inv }),
    analyze: useMutation({ mutationFn: (id: string) => api.analyzeParcelKw(id) }),
    fetchEkw: useMutation({ mutationFn: () => api.fetchKwFromEkw(parcelId!) }),
    paste: useMutation({
      mutationFn: (v: { text: string; kwNumber?: string; sections?: Record<string, string> }) =>
        api.pasteKwContent(parcelId!, v.text, v.kwNumber, v.sections),
      onSuccess: inv,
    }),
    applyOwners: useMutation({
      mutationFn: (v: { id: string; owners: KwOwnerRow[] }) => api.applyKwOwners(v.id, v.owners),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteParcelKw(id), onSuccess: inv }),
  };
}

// Latest generated documents for a set of contexts (one row per context, newest render only).
export const useGeneratedDocs = (contextKind: string, contextIds: string[], enabled = true) =>
  useQuery({
    queryKey: qk.generatedDocs(contextKind, contextIds),
    queryFn: () => api.getGeneratedDocs(contextKind, contextIds),
    enabled: enabled && contextIds.length > 0,
  });

// Pakiety WPW MASOWE per działka — automatyczne komplety wejściowe zapotrzebowania WPW.
export const useWpwPackages = (connectionId: string, enabled = true, includeOutdated = false) =>
  useQuery({
    // Prefiks klucza bez flagi — dotychczasowe invalidacje ["pp","wpw-packages",id]
    // odświeżają oba warianty.
    queryKey: ["pp", "wpw-packages", connectionId, includeOutdated ? "with-outdated" : "active"] as const,
    queryFn: () => api.getWpwPackages(connectionId, includeOutdated),
    enabled: enabled && !!connectionId,
    staleTime: 30_000,
  });

// Właściciele NIEAKTUALNI działki (ponowny wypis) — karta ich nie pokazuje; checkbox
// „pokaż nieaktualnych (N)" dociąga ich tym zapytaniem.
export const useParcelOutdatedOwners = (parcelId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "parcel-owners", parcelId, "outdated"] as const,
    queryFn: () => api.getParcelOwners(parcelId, true).then((rows) => rows.filter((o) => o.outdated_at)),
    enabled: enabled && !!parcelId,
  });

// Pełna historia generatów jednego kontekstu (najnowszy pierwszy). Klucz pod prefiksem
// "generated-docs", więc istniejące invalidacje po generacji odświeżają też historię.
export const useGeneratedHistory = (contextKind: string, contextId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "generated-docs", "history", contextKind, contextId] as const,
    queryFn: () => api.getGeneratedHistory(contextKind, contextId),
    enabled: enabled && !!contextId,
    staleTime: 30_000,
  });

export const useGenerateDocument = () =>
  useMutation({
    mutationFn: (v: {
      templateId: string;
      contextKind: string;
      contextId: string;
      overrides?: Record<string, string>;
      emptyMode?: "blank" | "marker";
      persist?: string[];
    }) =>
      api.generateDocument(v.templateId, v.contextKind, v.contextId, {
        overrides: v.overrides,
        emptyMode: v.emptyMode,
        persist: v.persist,
      }),
  });

// ── TOM II — Tytuły prawne do nieruchomości ──────────────────────────────────
// Karta w zakładce „Dokumentacja": bramka, szablony, raport kompletności i ostatni
// generat. Raport liczy się z bazy (bez otwierania plików), więc karta ładuje się
// od razu; świeżość 30 s, bo braki zmieniają się razem z pracą na działkach.
export const useTomTitles = (connectionId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "tom-titles", connectionId] as const,
    queryFn: () => api.getTomTitlesOverview(connectionId),
    enabled: enabled && !!connectionId,
    staleTime: 30_000,
  });

export const useGenerateTomTitles = (connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v?: { template_id?: string }) => api.generateTomTitles(connectionId, v),
    // Zadanie idzie w tle, więc świeży generat pojawi się dopiero przy kolejnym
    // odczycie — kartę i historię odświeża komponent po zakończeniu zadania.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "tom-titles", connectionId] }),
  });
};

// Załączniki do wniosku regulacyjnego (checklist per działka).
// Kandydaci do powiązania ze sprawą (zamówienia projektu + pakiety bez sprawy);
// pobierane dopiero po otwarciu pickera.
export const useProceedingLinkCandidates = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.proceedingLinkCandidates(id),
    queryFn: () => api.getProceedingLinkCandidates(id),
    enabled: enabled && !!id,
  });

// Działki sprawy do zaznaczenia przy rozstrzygnięciu częściowym (+ podpowiedź WPW ze
// zlecenia). Zawsze świeże — tytuły i zgłoszenia zlecenia zmieniają się poza sprawą.
export const useResolutionParcels = (id: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.resolutionParcels(id ?? ""),
    queryFn: () => api.getResolutionParcels(id as string),
    enabled: enabled && !!id,
    staleTime: 0,
  });

export const useRegulatoryChecklist = (parcelId: string, enabled = true) =>
  useQuery({
    queryKey: qk.regulatoryChecklist(parcelId),
    queryFn: () => api.getRegulatoryChecklist(parcelId),
    enabled: enabled && !!parcelId,
  });

export const useRegulatoryAttachmentMutations = (parcelId: string) => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.regulatoryChecklist(parcelId) });
  return {
    upload: useMutation({ mutationFn: api.uploadRegulatoryAttachment, onSuccess: inv }),
    remove: useMutation({ mutationFn: api.deleteRegulatoryAttachment, onSuccess: inv }),
  };
};

// Save the per-owner uwagi for the OMI regulatory application; refresh the owner in
// both parcel caches so #regulacja_uwagi# is up to date at the next generation.
export const useUpdateOwnerRegulatoryNotes = (parcelId: string, connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ownerId: string; notes: string }) =>
      api.updateOwnerRegulatoryNotes(v.ownerId, v.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
      qc.invalidateQueries({ queryKey: qk.parcels(connectionId) });
    },
  });
};

export function useDocTemplateMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.docTemplates });
    // Podmiana pliku dokłada wersję — odśwież też otwarte historie wersji.
    qc.invalidateQueries({ queryKey: ["pp", "doc-template-versions"] });
  };
  return {
    create: useMutation({
      mutationFn: (v: { name: string; kind?: string; operator?: string; description?: string; file: File }) =>
        api.createDocTemplate(v),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; name?: string; kind?: string; operator?: string; description?: string; file?: File }) =>
        api.updateDocTemplate(v.id, v),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteDocTemplate, onSuccess: inv }),
  };
}

// ── Anonimizacja PDF ─────────────────────────────────────────────────────────
// Zadanie biegnie w Celery (render + wywołania modelu per strona), więc lista
// odpytuje się co 3 s DOPÓKI cokolwiek jest w toku — i przestaje, gdy nic nie biegnie.
//
// Anulowanie stempluje wiersz „Anulowano" OD RAZU (endpoint), a przebieg jeszcze
// przez chwilę składa plik CZĘŚCIOWY z gotowych stron. Bez dodatkowego okna
// odnośnik „Wynik" pojawiałby się dopiero po ręcznym odświeżeniu — stąd
// odpytywanie trwa jeszcze kilka minut po anulowaniu, ale tylko wtedy, gdy była
// jakakolwiek gotowa strona (inaczej nie ma na co czekać).
const PARTIAL_RESULT_WINDOW_MS = 5 * 60_000;

function awaitingPartialResult(r: DocAnonymization): boolean {
  if (r.status !== "cancelled" || r.result_file || r.processed_pages === 0) return false;
  const finished = r.finished_at ? Date.parse(r.finished_at) : NaN;
  return Number.isFinite(finished) && Date.now() - finished < PARTIAL_RESULT_WINDOW_MS;
}

export const useDocAnonymizations = (all = false) =>
  useQuery({
    queryKey: [...qk.docAnonymizations, all] as const,
    queryFn: () => api.getDocAnonymizations(all),
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (r) => r.status === "pending" || r.status === "running" || awaitingPartialResult(r),
      )
        ? 3000
        : false,
  });

// Presety modeli (te same co w ARON-ie) — zmieniają się tylko przy zmianie configu.
export const useDocAnonymizationModels = () =>
  useQuery({
    queryKey: qk.docAnonymizationModels,
    queryFn: api.getDocAnonymizationModels,
    staleTime: 5 * 60_000,
  });

export function useDocAnonymizationMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.docAnonymizations });
  return {
    create: useMutation({
      mutationFn: (v: api.CreateDocAnonymizationInput) => api.createDocAnonymization(v),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: api.cancelDocAnonymization, onSuccess: inv }),
    remove: useMutation({ mutationFn: api.deleteDocAnonymization, onSuccess: inv }),
  };
}

// ── Formatki (plan sytuacyjny — DXF paper-space layouts) ─────────────────────
export const useSheetSets = (connection: string | undefined) =>
  useQuery({
    queryKey: qk.sheetSets(connection ?? ""),
    queryFn: () => api.getSheetSets(connection!),
    enabled: !!connection,
    // Poll while any set (incl. a freshly-created bundle) is generating, so the
    // list flips to „Wygenerowany" — and the upload/download controls appear —
    // without a manual refresh.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((s) => s.status === "generating") ? 3000 : false,
  });

export const useSheetSet = (id: string | undefined) =>
  useQuery({
    queryKey: qk.sheetSet(id ?? ""),
    queryFn: () => api.getSheetSet(id!),
    enabled: !!id,
    // Poll while generating (mirror of the list hook) — without this the plan
    // page keeps a stale „generating" row forever (refetchOnWindowFocus is off
    // globally and the generate modal's settle-refetch dies with the modal),
    // leaving the template select and edit buttons greyed until a hard reload.
    refetchInterval: (q) => (q.state.data?.status === "generating" ? 3000 : false),
  });

// Every sheet-set mutation returns the full row — write it into the detail key
// and refresh the connection's list so FormatkiSectionCard stays current.
function useSheetSetWrite<TVars>(mutationFn: (v: TVars) => Promise<FormatkaSheetSet>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      qc.setQueryData(qk.sheetSet(data.id), data);
      qc.invalidateQueries({ queryKey: qk.sheetSets(data.connection) });
    },
  });
}

export const useCreateSheetSet = () => useSheetSetWrite(api.createSheetSet);

// Wsad per działka: odpowiedź niesie WSZYSTKIE założone zestawy (udane i z błędem) —
// każdy trafia do klucza szczegółu, a lista projektu odświeża się raz.
export const useCreateSheetSetsPerParcel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createSheetSetsPerParcel,
    onSuccess: (data) => {
      for (const s of data.sets) qc.setQueryData(qk.sheetSet(s.id), s);
      const connection = data.sets[0]?.connection;
      if (connection) qc.invalidateQueries({ queryKey: qk.sheetSets(connection) });
    },
  });
};

// „Ponów rozplanowanie" jednego zestawu z błędem — opcjonalnie na innej wersji trasy.
export const useRetrySheetSet = (id: string) =>
  useSheetSetWrite((routeVersion?: string) => api.retrySheetSet(id, routeVersion));

// „Ponów generowanie ZIP" dla kilku zestawów naraz (wiersze z błędem generowania):
// każdy dostaje własne zadanie w tle (serwer odsiewa duplikaty per zestaw); 400 na
// jednym (brak szablonu, brak DXF-a wersji) nie przerywa reszty — wraca w liczniku
// z pierwszym powodem. Lista projektu odświeża się raz i zaczyna polling.
export const useGenerateSheetSets = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      let ok = 0;
      let failed = 0;
      let firstError = "";
      for (const id of ids) {
        try {
          await api.generateSheetSet(id);
          ok += 1;
        } catch (e) {
          failed += 1;
          if (!firstError) firstError = apiErr(e);
        }
      }
      return { ok, failed, total: ids.length, firstError };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.sheetSets(connection) }),
  });
};

// „Ponów wszystkie": po kolei każdy zestaw z błędem rozplanowania. Jeden nadal
// nieudany nie przerywa reszty (serwer odpowiada 200 w obu przypadkach, `ok` mówi,
// czy ramki się znalazły). Lista projektu odświeża się raz, po całej serii.
export const useRetrySheetSets = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { ids: string[]; routeVersion?: string }) => {
      let ok = 0;
      for (const id of v.ids) {
        const set = await api.retrySheetSet(id, v.routeVersion);
        qc.setQueryData(qk.sheetSet(id), set);
        if (set.ok) ok += 1;
      }
      return { ok, total: v.ids.length };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.sheetSets(connection) }),
  });
};

export const useUpdateSheetSet = (id: string) =>
  useSheetSetWrite((v: Parameters<typeof api.updateSheetSet>[1]) => api.updateSheetSet(id, v));

export const useSaveSheetFrames = (id: string) =>
  useSheetSetWrite((frames: SheetFrame[]) => api.saveSheetFrames(id, frames));

export const useReseedSheetSet = (id: string) =>
  useSheetSetWrite(() => api.reseedSheetSet(id));

export const useGenerateSheetSet = (id: string, connection?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateSheetSet(id),
    // Status flipped to "generating" server-side — refetch the row (and, when
    // triggered from the list card, the connection's list so it starts polling).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.sheetSet(id) });
      if (connection) qc.invalidateQueries({ queryKey: qk.sheetSets(connection) });
    },
  });
};

export const useDeleteSheetSet = (connection: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSheetSet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sheetSets(connection) }),
  });
};

// Upload plotted PDFs → merged version; or director overwrite. Both return the
// updated sheet set → written into detail + list by useSheetSetWrite.
export const useUploadSheetPdfs = () =>
  useSheetSetWrite((v: { id: string; files: File[] }) => api.uploadSheetSetPdfs(v.id, v.files));

export const useOverwriteSheetPdf = () =>
  useSheetSetWrite((v: { id: string; file: File; note?: string }) =>
    api.overwriteSheetSetPdf(v.id, v.file, v.note),
  );

// Create a bundle set from the selected sets and start its generation. The new
// set is written into the detail + connection list (which then polls it).
export const useBundleSheetSets = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.bundleSheetSets(ids),
    onSuccess: (data) => {
      qc.setQueryData(qk.sheetSet(data.id), data);
      qc.invalidateQueries({ queryKey: qk.sheetSets(data.connection) });
    },
  });
};

export const useFormatkaTemplates = () =>
  useQuery({ queryKey: qk.formatkaTemplates, queryFn: api.getFormatkaTemplates });

// Static variable registry (tag → description) — cached for the session.
export const useFormatkaVariables = (enabled = true) =>
  useQuery({
    queryKey: qk.formatkaVariables,
    queryFn: api.getFormatkaVariables,
    staleTime: Infinity,
    enabled,
  });

export const useFormatkaTemplateVersions = (templateId: string | undefined) =>
  useQuery({
    queryKey: qk.formatkaTemplateVersions(templateId ?? ""),
    queryFn: () => api.getFormatkaTemplateVersions(templateId!),
    enabled: !!templateId,
  });

export function useFormatkaTemplateMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.formatkaTemplates });
    // Podmiana pliku dokłada wersję — odśwież też otwarte historie wersji.
    qc.invalidateQueries({ queryKey: ["pp", "formatka-template-versions"] });
  };
  return {
    create: useMutation({ mutationFn: api.createFormatkaTemplate, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: {
        id: string; name?: string; description?: string; operator?: string;
        paper?: import("./types").FormatkaPaper; file?: File;
      }) => api.updateFormatkaTemplate(v.id, v),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteFormatkaTemplate, onSuccess: inv }),
  };
}

// Siatka domyślnych szablonów (operator × przeznaczenie). Zapis unieważnia też
// listę szablonów — komórka trzyma nazwę szablonu, którą lista renderuje.
export const useFormatkaDefaults = (enabled = true) =>
  useQuery({ queryKey: qk.formatkaDefaults, queryFn: api.getFormatkaDefaults, enabled });

export function useSaveFormatkaDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.saveFormatkaDefaults,
    onSuccess: (data) => qc.setQueryData(qk.formatkaDefaults, data),
  });
}

export const useFormatkaTemplateCheck = (templateId: string, sheetSetId: string) =>
  useQuery({
    queryKey: qk.formatkaCheck(templateId, sheetSetId),
    queryFn: () => api.checkFormatkaTemplate(templateId, sheetSetId),
    enabled: !!templateId && !!sheetSetId,
  });

export const useFormatkaFieldDefaults = (enabled = true) =>
  useQuery({
    queryKey: qk.formatkaFieldDefaults,
    queryFn: api.getFormatkaFieldDefaults,
    enabled,
  });

// Wartości pól tabelki zapamiętane per projekt („zapamiętaj dla projektu").
export const useFormatkaProjectFields = (connectionId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "formatka-project-fields", connectionId] as const,
    queryFn: () => api.getFormatkaProjectFields(connectionId),
    enabled: enabled && !!connectionId,
  });

export const useSaveFormatkaProjectFields = (connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, string>) =>
      api.saveFormatkaProjectFields(connectionId, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "formatka-project-fields", connectionId] });
      // odśwież prefill pól ręcznych (auto/manual liczą wartości zapamiętane)
      qc.invalidateQueries({ queryKey: ["pp", "formatka-manual-fields"] });
    },
  });
};

// Podział #zmiennych# szablonu: auto vs ręczne (modal szybkiej generacji z wniosku).
export const useFormatkaManualFields = (templateId: string, connectionId: string) =>
  useQuery({
    queryKey: ["pp", "formatka-manual-fields", templateId, connectionId] as const,
    queryFn: () => api.getFormatkaManualFields(templateId, connectionId),
    enabled: !!templateId && !!connectionId,
  });

// ── masowe pakiety WPW (formularze ENERGA) ─────────────────────────────────────
export const useWpwMassOverview = (ctx: import("./api").WpwMassCtx | null, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wpw-mass-overview", ctx] as const,
    queryFn: () => api.getWpwMassOverview(ctx!),
    enabled: enabled && !!ctx,
  });

// Dodatkowe strony A4 pakietów (przed formatkami A3) — panel w kroku „Generuj i kompiluj".
// Osobne, lekkie zapytanie: overview jest ciężkie, a lista plików zmienia się często.
export const useWpwMassExtraFiles = (ctx: import("./api").WpwMassCtx | null, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wpw-mass-extra-files", ctx] as const,
    queryFn: () => api.getWpwMassExtraFiles(ctx!),
    enabled: enabled && !!ctx,
  });

export function useWpwMassExtraFileMutations(ctx: import("./api").WpwMassCtx | null) {
  const qc = useQueryClient();
  const c = ctx as import("./api").WpwMassCtx;
  // Każda mutacja zwraca pełną listę — wpisujemy ją do cache od razu; overview też
  // się odświeża (gotowość „package_stale" pakietów zależy od tej listy).
  const settle = (res: api.WpwMassExtraFilesResponse) => {
    qc.setQueryData(["pp", "wpw-mass-extra-files", ctx], res);
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview", ctx] });
  };
  return {
    upload: useMutation({
      mutationFn: (v: { files: File[]; parcelIds?: string[]; placement?: import("./types").WpwExtraPagePlacement }) =>
        api.uploadWpwMassExtraFiles(c, v.files, { parcelIds: v.parcelIds, placement: v.placement }),
      onSuccess: settle,
    }),
    patch: useMutation({
      mutationFn: ({ id, ...body }: {
        id: string; label?: string; all_parcels?: boolean; parcel_ids?: string[]; position?: number;
        placement?: import("./types").WpwExtraPagePlacement;
      }) => api.patchWpwMassExtraFile(id, body),
      onSuccess: settle,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteWpwMassExtraFile(id),
      onSuccess: settle,
    }),
  };
}

// Globalna lista sekwencji masowych WPW w toku (aktywne szkice) — panel „w toku".
export const useWpwSequences = (enabled = true) =>
  useQuery({
    queryKey: ["pp", "wpw-sequences"] as const,
    queryFn: api.getWpwSequences,
    enabled,
  });

// Rozkład #zmiennych# szablonów WNU właściciela (współdzielony klucz z inline panelem pól).
export const useWpwMassParcelFields = (parcelId: string, owner?: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wpw-mass-parcel-fields", parcelId, owner ?? ""] as const,
    queryFn: () => api.getWpwMassParcelFields(parcelId, owner),
    enabled: enabled && !!parcelId,
  });

// Opis urządzeń (kubełki per status) i opis ENERGA są cechą DZIAŁKI, a wiersz overview
// jest per (działka, właściciel) — działka z kilkoma współwłaścicielami występuje w tabeli
// kilka razy. Po zapisie w jednym wierszu wstrzykujemy zapisane pola do KAŻDEGO wiersza
// tej działki od razu, zanim wróci (długi) refetch overview — inaczej pola pozostałych
// współwłaścicieli pokazywały starą treść przez kilka sekund (zgłoszenie 10.09.2026).
// Dobór formularzy (form_kinds) też wchodzi od razu — w onMutate, zanim wróci PATCH
// (zgłoszenie 16.09.2026: checkbox przestawiał się dopiero po kilku sekundach refetchu,
// co wyglądało jak zepsute klikanie w etykietę). effective_form_kinds liczymy tą samą
// regułą co backend (breakdown.effective_form_kinds): dobór ręczny wygrywa, pusta lista
// = dobór automatyczny z mapy (auto_form_kinds wiersza).
// Zatwierdzenie zakresu z etykiet CAD (scope_review) też: odpowiedź scope-confirm niesie
// świeży stan, a bez wpisania go do cache każdy wiersz działki pokazywał wątpliwości aż do
// końca ciężkiego refetchu (zgłoszenie 17.09.2026).
export function applyWpwParcelPatch(
  overview: WpwMassOverview | undefined,
  parcelId: string,
  updated: api.WpwMassParcelPatchResult & { scope_review?: WpwMassScopeReview },
): WpwMassOverview | undefined {
  if (!overview) return overview;
  const patch: Partial<WpwMassOverview["rows"][number]["parcel"]> = {};
  if (updated.scope_review) patch.scope_review = updated.scope_review;
  if (updated.consent_work_descriptions) {
    patch.consent_work_descriptions = updated.consent_work_descriptions;
    patch.consent_description_sources = updated.consent_description_sources ?? {};
  }
  if (updated.energa_work_description !== undefined) {
    patch.energa_work_description = updated.energa_work_description;
  }
  const kinds = updated.form_kinds;
  if (Object.keys(patch).length === 0 && !kinds) return overview;
  let touched = false;
  const rows = overview.rows.map((row) => {
    if (row.parcel.id !== parcelId) return row;
    touched = true;
    const next = { ...row, parcel: { ...row.parcel, ...patch } };
    if (kinds) {
      next.form_kinds = kinds;
      next.effective_form_kinds = kinds.length > 0 ? kinds : row.auto_form_kinds;
    }
    // Zatwierdzony zakres zdejmuje blokadę „zakres do zatwierdzenia" z listy blokad wiersza.
    if (updated.scope_review && !updated.scope_review.needed) {
      next.blocked = row.blocked.filter((b) => b.code !== "scope_review_needed");
    }
    return next;
  });
  return touched ? { ...overview, rows } : overview;
}

// ── zatwierdzanie zakresu z etykiet CAD ──────────────────────────────────────
export const SCOPE_CONFIRM_KEY = ["pp", "wpw-scope-confirm"] as const;
export type WpwScopeConfirmItem = { parcelId: string; form_kinds?: string[]; note?: string };

/** Działki, których zatwierdzenie zakresu właśnie trwa — z obu mutacji (pojedynczej i
 *  zbiorczej). useMutation pamięta tylko OSTATNIE wywołanie, a zatwierdzać można kilka
 *  działek naraz: dotąd spinner znikał z pierwszej, gdy kliknięto drugą. */
export function useScopeConfirmPendingIds(): Set<string> {
  const vars = useMutationState({
    filters: { mutationKey: SCOPE_CONFIRM_KEY, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as { parcelId?: string; items?: WpwScopeConfirmItem[] } | undefined,
  });
  return useMemo(() => {
    const ids = new Set<string>();
    for (const v of vars) {
      if (!v) continue;
      if (v.parcelId) ids.add(v.parcelId);
      for (const item of v.items ?? []) ids.add(item.parcelId);
    }
    return ids;
  }, [vars]);
}

/** Uruchom `fn` dla każdego elementu, najwyżej `limit` naraz (kolejność startu zachowana). */
export async function runLimited<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      await fn(item);
    }
  });
  await Promise.all(workers);
}

export function useWpwMassMutations(ctx: import("./api").WpwMassCtx | null) {
  const qc = useQueryClient();
  const c = ctx as import("./api").WpwMassCtx; // gated: mutacje wołane tylko gdy ctx istnieje
  const inv = () => qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview", ctx] });
  return {
    invalidate: inv,
    patchParcel: useMutation({
      mutationFn: (v: {
        parcelId: string;
        consent_work_descriptions?: Record<string, string>;
        energa_work_description?: string;
        form_kinds?: string[];
      }) => api.patchWpwMassParcel(v.parcelId, v),
      // Dobór formularzy: do cache'u JUŻ przy kliknięciu — checkbox ma się przestawić
      // od razu, nie po refetchu overview (kilka sekund przy dużym projekcie, a przez ten
      // czas React trzymał kontrolowany input w starym stanie). Błąd cofa do migawki.
      onMutate: async (v) => {
        if (!v.form_kinds) return undefined;
        const queryKey = ["pp", "wpw-mass-overview"];
        await qc.cancelQueries({ queryKey });
        const snapshot = qc.getQueriesData<WpwMassOverview>({ queryKey });
        qc.setQueriesData<WpwMassOverview>(
          { queryKey },
          (old) => applyWpwParcelPatch(old, v.parcelId, { form_kinds: v.form_kinds }),
        );
        return { snapshot };
      },
      onError: (_err, _v, context) => {
        context?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
      },
      onSuccess: (updated, v) => {
        // Najpierw cache: wszystkie wiersze działki (każdy współwłaściciel) dostają
        // zapisany opis natychmiast; refetch poniżej tylko to potwierdza.
        qc.setQueriesData<WpwMassOverview>(
          { queryKey: ["pp", "wpw-mass-overview"] },
          (old) => applyWpwParcelPatch(old, v.parcelId, updated),
        );
        inv();
        // opis/dobór formularzy zmienia pola szablonów (#…_prv_ai#, liczniki „do uzupełnienia")
        qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-parcel-fields"] });
      },
    }),
    acceptStaleMap: useMutation({
      mutationFn: (parcelId: string) => api.acceptWpwStaleMap(parcelId),
      onSuccess: inv,
    }),
    ensureApplications: useMutation({
      mutationFn: (parcelIds: string[]) => api.wpwMassEnsureApplications(c, parcelIds),
      onSuccess: inv,
    }),
    // Zakres zgód: zwolnienie z wymogu analizy KW i wybór, kto jedzie w teren.
    // Odświeżamy całe overview — bramka KW wisi na KAŻDYM wierszu działki, a suma
    // udziałów zmienia się po każdym zaznaczeniu.
    kwGateOverride: useMutation({
      mutationFn: (v: { parcelId: string } & ({ reason: string } | { clear: true } | { skip: true })) =>
        api.setWpwKwGateOverride(
          v.parcelId,
          "clear" in v ? { clear: true } : "skip" in v ? { skip: true } : { reason: v.reason },
        ),
      onSuccess: inv,
    }),
    // Zatwierdzenie zakresu prac, gdy analiza etykiet CAD zostawiła wątpliwości —
    // zdejmuje blokadę scope_review_needed z każdego wiersza działki.
    confirmScope: useMutation({
      mutationKey: SCOPE_CONFIRM_KEY,
      mutationFn: (v: { parcelId: string; confirm: boolean; form_kinds?: string[]; note?: string }) =>
        api.confirmWpwScope(v.parcelId, { confirm: v.confirm, form_kinds: v.form_kinds, note: v.note }),
      // Świeży stan zatwierdzenia wraca w odpowiedzi — wchodzi do KAŻDEGO wiersza działki od
      // razu (dotyczy całej działki, nie jednego współwłaściciela); refetch tylko potwierdza.
      onSuccess: (updated, v) => {
        qc.setQueriesData<WpwMassOverview>(
          { queryKey: ["pp", "wpw-mass-overview"] },
          (old) => applyWpwParcelPatch(old, v.parcelId, updated),
        );
        inv();
      },
    }),
    // „Zatwierdź zakres wszystkich": kilka żądań naraz (limit równoległości — każde liczy
    // rozbicie mapy działki), każdy sukces wchodzi do cache natychmiast, porażki wracają w
    // WYNIKU zamiast wyjątkiem, żeby jedna zła działka nie ukryła reszty. Refetch raz, na końcu.
    confirmScopes: useMutation({
      mutationKey: SCOPE_CONFIRM_KEY,
      mutationFn: async (v: { items: WpwScopeConfirmItem[] }) => {
        const confirmed: string[] = [];
        const failed: { parcelId: string; error: string }[] = [];
        await runLimited(v.items, 4, async (item) => {
          try {
            const updated = await api.confirmWpwScope(item.parcelId, {
              confirm: true, form_kinds: item.form_kinds, note: item.note,
            });
            qc.setQueriesData<WpwMassOverview>(
              { queryKey: ["pp", "wpw-mass-overview"] },
              (old) => applyWpwParcelPatch(old, item.parcelId, updated),
            );
            confirmed.push(item.parcelId);
          } catch (err) {
            failed.push({ parcelId: item.parcelId, error: extractApiError(err) || "Nie udało się zatwierdzić zakresu." });
          }
        });
        return { confirmed, failed };
      },
      onSettled: inv,
    }),
    fieldExclusion: useMutation({
      mutationFn: (v: { parcelId: string; owners: string[]; excluded: boolean; reason?: string }) =>
        api.setWpwFieldExclusion(v.parcelId, {
          owners: v.owners, excluded: v.excluded, reason: v.reason,
        }),
      onSuccess: inv,
    }),
    describe: useMutation({
      mutationFn: (v: { parcelIds: string[]; force?: boolean }) =>
        api.wpwMassDescribe(c, v.parcelIds, { force: v.force ?? false }),
    }),
    // Generacja od razu kompiluje pakiety WNU, którym wyszły wszystkie formularze —
    // stąd `duplex` jak w `compile`.
    generateForms: useMutation({
      mutationFn: (v: { parcelIds: string[]; duplex?: boolean }) =>
        api.wpwMassGenerateForms(c, v.parcelIds, { duplex: v.duplex ?? false }),
    }),
    compile: useMutation({
      mutationFn: (v: { parcelIds: string[]; duplex?: boolean }) =>
        api.wpwMassCompile(c, v.parcelIds, { duplex: v.duplex ?? false }),
    }),
    handoff: useMutation({
      mutationFn: (parcelIds: string[]) => api.wpwMassHandoff(c, parcelIds),
    }),
    // Cała sekwencja jednym kliknięciem: WNU → opisy AI → plan sytuacyjny →
    // formularze → kompilacja. Endpoint istniał od dawna, ale NIE MIAŁ w aplikacji
    // ani jednego wywołania — jedynym jego użytkownikiem był ARON, więc ścieżka
    // nigdy nie przeszła przez ludzkie ręce (EOP 64, 31.08–01.09.2026).
    prepareAll: useMutation({
      mutationFn: (parcelIds: string[]) => {
        // Sekwencja jest order-free — istnieje wyłącznie w trybie „przedsięwzięcie".
        if (!("connection" in c)) {
          return Promise.reject(new Error("Sekwencja masowa działa tylko w trybie przedsięwzięcia."));
        }
        return api.wpwMassPrepare(c.connection, parcelIds);
      },
    }),
  };
}

// ── zwroty WPW z terenu ────────────────────────────────────────────────────────
export const useWpwReturnBatches = (params: { connection?: string; order?: string }, enabled = true) =>
  useQuery({
    queryKey: ["pp", "wpw-return-batches", params] as const,
    queryFn: () => api.getWpwReturnBatches(params),
    enabled: enabled && !!(params.connection || params.order),
  });

export const useWpwReturnBatch = (id: string | null) =>
  useQuery({
    queryKey: ["pp", "wpw-return-batch", id ?? ""] as const,
    queryFn: () => api.getWpwReturnBatch(id!),
    enabled: !!id,
  });

export function useWpwReturnMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "wpw-return-batches"] });
    qc.invalidateQueries({ queryKey: ["pp", "wpw-return-batch"] });
    // Zastosowanie zwrotu zmienia status wniosku (→ „zwrot ✓” w wierszu działki) i
    // przekazania — odśwież overview, żeby propagowało bez F5.
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
  };
  return {
    invalidate: inv,
    upload: useMutation({
      mutationFn: (v: { orderId: string; file: File }) => api.uploadWpwReturnBatch(v.orderId, v.file),
      onSuccess: inv,
    }),
    segment: useMutation({ mutationFn: api.segmentWpwReturnBatch }),
    patchItem: useMutation({
      mutationFn: (v: { itemId: string; decision?: "accept" | "reject" | ""; note?: string; parcel_id?: string; pages?: string }) =>
        api.patchWpwReturnItem(v.itemId, v),
      onSuccess: inv,
    }),
    apply: useMutation({ mutationFn: api.applyWpwReturnBatch, onSuccess: inv }),
    cancel: useMutation({ mutationFn: api.cancelWpwReturnBatch, onSuccess: inv }),
  };
}

// ── Raporty okresowe do ZE ─────────────────────────────────────────────────────
export const useReports = (connection: string | undefined) =>
  useQuery({
    queryKey: qk.reports(connection ?? ""),
    queryFn: () => api.getReports(connection!),
    enabled: !!connection,
  });

/** Wyliczony harmonogram terminów — zależy od trasy i ustawień projektu, więc
 *  unieważniamy go razem z listą raportów i po zmianie ustawień raportowania. */
export const useReportSchedule = (connection: string | undefined) =>
  useQuery({
    queryKey: qk.reportSchedule(connection ?? ""),
    queryFn: () => api.getReportSchedule(connection!),
    enabled: !!connection,
  });

export const useReportMutations = (connection: string) => {
  const qc = useQueryClient();
  // Każda zmiana raportu może domknąć albo otworzyć termin → odśwież też harmonogram.
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.reports(connection) });
    qc.invalidateQueries({ queryKey: qk.reportSchedule(connection) });
    qc.invalidateQueries({ queryKey: qk.portfolioReportColumn });
  };
  return {
    create: useMutation({ mutationFn: api.createReport, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<ZEReport> }) => api.updateReport(v.id, v.payload),
      onSuccess: inv,
    }),
    addFiles: useMutation({
      mutationFn: (v: { id: string; files: File[] }) => api.addReportFiles(v.id, v.files),
      onSuccess: inv,
    }),
    markSent: useMutation({
      mutationFn: (v: { id: string; sent_at?: string }) => api.markReportSent(v.id, v.sent_at),
      onSuccess: inv,
    }),
    unmarkSent: useMutation({ mutationFn: (id: string) => api.unmarkReportSent(id), onSuccess: inv }),
    remove: useMutation({ mutationFn: api.deleteReport, onSuccess: inv }),
    // Zwraca task_id — postęp pokazuje useTaskPolling, a po zakończeniu odświeżamy
    // listę, bo szkic (plik + treść) siedzi na samym raporcie.
    generateDraft: useMutation({
      mutationFn: (v: { id: string; formats: ZEDocFormat[] }) =>
        api.generateReportDraft(v.id, v.formats),
      onSuccess: inv,
    }),
  };
};

// ── Wnioski o aneks do umowy z ZE ─────────────────────────────────────────────
export const useAnnexRequests = (connection: string | undefined) =>
  useQuery({
    queryKey: qk.annexRequests(connection ?? ""),
    queryFn: () => api.getAnnexRequests(connection!),
    enabled: !!connection,
  });

export const useAnnexMutations = (connection: string) => {
  const qc = useQueryClient();
  // Zawarty aneks przesuwa obowiązujący termin, a ten wchodzi do metryki raportu
  // i do kart projektu — dlatego unieważniamy też raporty i sam projekt.
  const inv = () => {
    qc.invalidateQueries({ queryKey: qk.annexRequests(connection) });
    qc.invalidateQueries({ queryKey: qk.reports(connection) });
    qc.invalidateQueries({ queryKey: qk.connection(connection) });
  };
  return {
    create: useMutation({ mutationFn: api.createAnnexRequest, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Partial<ZEAnnexRequest> }) =>
        api.updateAnnexRequest(v.id, v.payload),
      onSuccess: inv,
    }),
    addFiles: useMutation({
      mutationFn: (v: { id: string; files: File[] }) => api.addAnnexFiles(v.id, v.files),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteAnnexRequest, onSuccess: inv }),
    generateDraft: useMutation({ mutationFn: api.generateAnnexDraft, onSuccess: inv }),
    markSent: useMutation({
      mutationFn: (v: { id: string; sent_at?: string }) => api.markAnnexSent(v.id, v.sent_at),
      onSuccess: inv,
    }),
    unmarkSent: useMutation({ mutationFn: (id: string) => api.unmarkAnnexSent(id), onSuccess: inv }),
    resolve: useMutation({
      mutationFn: (v: {
        id: string;
        payload: Parameters<typeof api.resolveAnnexRequest>[1];
      }) => api.resolveAnnexRequest(v.id, v.payload),
      onSuccess: inv,
    }),
    reopen: useMutation({ mutationFn: (id: string) => api.reopenAnnexRequest(id), onSuccess: inv }),
  };
};

// ── mpzp orders (wypis i wyrys z planu miejscowego) ───────────────────────────
// 60s refetch — jak przy wypisach i MZ: opłacona opłata pojawia się bez reloadu.
export function useMpzpOrders(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.mpzpOrders(filters),
    queryFn: () => api.getMpzpOrders(filters),
    refetchInterval: 60_000,
  });
}

export function useMpzpOrder(id: string | undefined) {
  return useQuery({
    queryKey: qk.mpzpOrder(id ?? ""),
    queryFn: () => api.getMpzpOrder(id!),
    enabled: !!id,
  });
}

export function useMpzpOrdersForParcel(parcelId: string) {
  return useQuery({
    queryKey: qk.mpzpOrdersForParcel(parcelId),
    queryFn: () => api.getMpzpOrdersForParcel(parcelId),
  });
}

export function useMpzpCandidates(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.mpzpCandidates(filters),
    queryFn: () => api.getMpzpCandidates(filters),
  });
}

export const useMpzpPoaFeeSuggestion = (orderId: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "mpzp-poa-fee-suggestion", orderId] as const,
    queryFn: () => api.getMpzpPoaFeeSuggestion(orderId),
    enabled,
  });

export function useMpzpOrderMutations(orderId?: string) {
  const qc = useQueryClient();
  const inv = () => {
    if (orderId) qc.invalidateQueries({ queryKey: qk.mpzpOrder(orderId) });
    qc.invalidateQueries({ queryKey: ["pp", "mpzp-orders"] });
    // Sekcja „Wypisy MPZP" na karcie działki ma osobny klucz (prefiks „mpzp-orders"
    // go NIE obejmuje — to inny element tablicy), więc usunięcie/zakończenie
    // zamówienia musi ją odświeżyć jawnie.
    qc.invalidateQueries({ queryKey: ["pp", "mpzp-orders-for-parcel"] });
    // Zamówienie „trzyma" swoje działki, więc panel kandydatów musi się przeliczyć.
    qc.invalidateQueries({ queryKey: ["pp", "mpzp-candidates"] });
  };
  return {
    create: useMutation({ mutationFn: api.createMpzpOrder, onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.updateMpzpOrder>[1] }) =>
        api.updateMpzpOrder(v.id, v.payload),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteMpzpOrder(id), onSuccess: inv }),
    generate: useMutation({ mutationFn: (id: string) => api.generateMpzpDocuments(id) }),
    requestFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.requestMpzpFee>[1] }) =>
        api.requestMpzpFee(v.id, v.payload),
      onSuccess: inv,
    }),
    editDoo: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.editMpzpDoo>[1] }) =>
        api.editMpzpDoo(v.id, v.payload),
      onSuccess: inv,
    }),
    uploadFeeLetter: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadMpzpFeeLetter(v.id, v.file),
    }),
    setPoaFee: useMutation({
      mutationFn: (v: { id: string; payload: Parameters<typeof api.setMpzpPoaFee>[1] }) =>
        api.setMpzpPoaFee(v.id, v.payload),
      onSuccess: inv,
    }),
    markSent: useMutation({ mutationFn: (id: string) => api.markMpzpSent(id), onSuccess: inv }),
    markReceived: useMutation({
      mutationFn: (id: string) => api.markMpzpReceived(id),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelMpzpOrder(id), onSuccess: inv }),
    changeOrderer: useMutation({
      mutationFn: (v: { id: string; userId: string }) => api.changeMpzpOrderer(v.id, v.userId),
      onSuccess: inv,
    }),
    complete: useMutation({ mutationFn: (id: string) => api.completeMpzpOrder(id), onSuccess: inv }),
    revert: useMutation({
      mutationFn: (v: { id: string; reason?: string }) => api.revertMpzpOrder(v.id, v.reason),
      onSuccess: inv,
    }),
    uploadResultFiles: useMutation({
      mutationFn: (v: { id: string; files: File[]; other?: boolean }) =>
        api.uploadMpzpResultFiles(v.id, v.files, v.other),
      onSuccess: inv,
    }),
    deleteResultFile: useMutation({
      mutationFn: (v: { id: string; resultFileId: string }) =>
        api.deleteMpzpResultFile(v.id, v.resultFileId),
      onSuccess: inv,
    }),
  };
}

/**
 * Wypis i wyrys z MPZP pozyskany ręcznie poza ELGW5 — dodawany z karty działki samymi
 * plikami. Odświeża sekcję działki, samą działkę i listę działek połączenia (skrót
 * `mpzp_orders` zasila filtr „Wypis MPZP") oraz rejestr zamówień i kandydatów.
 */
export function useAddManualMpzpOrder(parcelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => api.addManualMpzpOrder(parcelId, files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mpzpOrdersForParcel(parcelId) });
      qc.invalidateQueries({ queryKey: qk.parcel(parcelId) });
      qc.invalidateQueries({ queryKey: ["pp", "parcels"] });
      qc.invalidateQueries({ queryKey: ["pp", "mpzp-orders"] });
      qc.invalidateQueries({ queryKey: ["pp", "mpzp-candidates"] });
    },
  });
}

/**
 * Uzupełnianie danych działek projektu. Cztery różne źródła, każde w tle:
 * dane administracyjne (ULDK), adresy (geokoder), pokrycie MPZP (raster), GDOŚ (WFS).
 * Wszystkie zwracają task_id — postęp pokazuje useTaskPolling.
 */
export function useParcelDataEnrichment() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["pp", "mpzp-candidates"] });
  return {
    adminData: useMutation({ mutationFn: api.fetchConnectionAdminData }),
    streets: useMutation({
      mutationFn: (v: { connectionId: string; force?: boolean }) =>
        api.fetchConnectionStreets(v.connectionId, v.force),
    }),
    mpzpCoverage: useMutation({
      mutationFn: (v: { connectionId: string; force?: boolean }) =>
        api.fetchConnectionMpzpCoverage(v.connectionId, v.force),
    }),
    protectedAreas: useMutation({ mutationFn: api.fetchConnectionProtectedAreas }),
    invalidate: inv,
  };
}

// ── preanaliza dokumentów z urzędów ───────────────────────────────────────────
/** Lista rozpoznań. Dopóki cokolwiek stoi w kolejce lub jest analizowane, odpytujemy
 *  co 3 s — wynik dopisuje zadanie w tle, więc bez tego ekran stałby na „w kolejce". */
export function useOfficeDocScans(filters: Record<string, string> = {}) {
  return useQuery({
    queryKey: qk.officeDocScans(filters),
    queryFn: () => api.getOfficeDocScans(filters),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((s) => s.status === "pending" || s.status === "running")
        ? 3_000
        : false,
  });
}

/** Paczki („to przyszło jednym mailem") — wspólny wynik ponad plikami. Odpytujemy
 *  tak samo jak listę dokumentów, dopóki którakolwiek paczka jest w analizie. */
export function useOfficeDocBatches() {
  return useQuery({
    queryKey: qk.officeDocBatches,
    queryFn: () => api.getOfficeDocBatches(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status === "running") ? 3_000 : false,
  });
}

export function useOfficeDocScanMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "office-docs"] });
    qc.invalidateQueries({ queryKey: qk.officeDocBatches });
  };
  return {
    upload: useMutation({
      mutationFn: (v: { files: File[]; asBatch?: boolean; label?: string }) =>
        api.uploadOfficeDocs(v.files, v.asBatch, v.label),
      onSuccess: inv,
    }),
    reanalyze: useMutation({ mutationFn: (id: string) => api.reanalyzeOfficeDoc(id), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteOfficeDoc(id), onSuccess: inv }),
    group: useMutation({
      mutationFn: (v: { ids: string[]; label?: string; batch?: string }) =>
        api.groupOfficeDocs(v.ids, v.label, v.batch),
      onSuccess: inv,
    }),
    ungroup: useMutation({ mutationFn: (id: string) => api.ungroupOfficeDoc(id), onSuccess: inv }),
  };
}

// ── kalendarz ponagleń (telefony kontrolne) ────────────────────────────────────
// Bez pollingu: kalendarz przelicza się po odklikaniu ponaglenia, a sprawy zmieniają
// stan na swoich stronach — odświeżamy przy powrocie na kartę.
export function useControlCalls(params: {
  from: string;
  to: string;
  scope: "mine" | "all";
  show: string;
  kinds: string;
  /** Podgląd dyrekcji: czyje zestawienie oglądamy (pusty = własne). */
  user?: string;
}) {
  return useQuery({
    queryKey: ["pp", "control-calls", params] as const,
    // Pusty `user` nie może iść w URL-u — backend odróżnia „brak parametru" (moje)
    // od wskazania osoby.
    queryFn: () =>
      api.getControlCalls(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<string, string>,
      ),
  });
}

/** Pracownicy, których zestawienie ponagleń może obejrzeć dyrektor. */
export function useSelectableEmployees(enabled: boolean) {
  return useQuery({
    queryKey: ["pp", "selectable-employees"] as const,
    queryFn: () => api.getSelectableEmployees(),
    enabled,
  });
}

export function useMarkControlCallDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.markControlCallDone(id, note),
    // Odklikanie planuje NASTĘPNE ponaglenie po stronie serwera, więc przeładowujemy
    // cały zakres zamiast łatać jeden wiersz w cache.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "control-calls"] }),
  });
}

/** Ponaglenie własne: dodanie i usunięcie. Ponaglenie „na dziś" zmienia plakietkę menu. */
export function useControlCallManual() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["pp", "control-calls"] });
    qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
  };
  return {
    create: useMutation({ mutationFn: api.createControlCall, onSuccess: inv }),
    remove: useMutation({ mutationFn: api.deleteControlCall, onSuccess: inv }),
  };
}
